import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Gift, Users, MessageCircle } from 'lucide-react';
import DealAnimation from './DealAnimation';
import { subscribeToMessages, ChatMessage } from '../services/chatService';
import { GameState, Card, SpecialRules } from '../types/GameState';
import gameEngine from '../services/gameEngine';
import * as tableService from '../services/tableService';
import GameResultNotification from './GameResultNotification';
//  ENTERPRISE: Import secure API service

import gameApiService from '../services/gameApiService';
import PokerTableSVG from './PokerTableSVG';
import { pssBustForBot, stripEmoji } from '../utils/pssBots';
import TableChat from './TableChat';
import WinnerCelebration from './WinnerCelebration';
import TableRulesModal from './TableRulesModal';
import voiceAnnouncer from '../utils/VoiceAnnouncer';
import '../styles/PokerTable.css';
import '../styles/TimerShowdown.css'; // ⏱ Turn timer &  Runout suspense styles

//  GAME ITEMS IMPORTS

import GameItemsMenu from './GameItemsMenu';
import PlayerActionAnimation from './PlayerActionAnimation';
import gameItemsService from '../services/gameItemsService';
import walletService from '../services/walletService';
import { ActiveAnimation } from '../types/gameItemsTypes';
import '../styles/GameItems.css';
import UserSearch from './UserSearch';
import BuyInModal from './BuyInModal';
//  STORIES: Share Win Modal

import ShareWinModal from './stories/ShareWinModal';
import { isShareableWin } from '../services/storyService';
import { auth } from '../services/firebase';
import RabbitHuntModal from './RabbitHuntModal';

// ============================================================================
//  SOUND SERVICE - Cross-platform audio (Capacitor native + web fallback)

// ============================================================================
import soundService from '../services/soundService';

// Legacy wrapper for compatibility - uses new soundService
const playActionSound = (action: string, enabled: boolean) => {
  if (!enabled) return;
  soundService.play(action);
};

// Still in the hand for the reveal: not folded, OR busted on this very
// hand. The server stamps a bust as folded+needsRebuy at hand end, but the
// cards were live to the river - skipping that seat is how an all-in runout
// against a busting opponent jumped straight to the result.
const inShowdownHand = (p: any): boolean =>
  !!p && (!p.folded || ((p.needsRebuy || p.eliminated) && Array.isArray(p.holeCards) && p.holeCards.length === 2 && !!p.holeCards[0]?.rank));

// One row per player: run-it-twice writes a winners[] row per run, side
// pots can add more. Amounts sum; the hand shown is the biggest share's.
const aggregateWinnerRows = (rows: any[]): any[] => {
  const byId = new Map<string, any>();
  for (const w of rows || []) {
    if (!w || !w.playerId) continue;
    const cur = byId.get(w.playerId);
    if (!cur) { byId.set(w.playerId, { ...w, amount: w.amount || 0, amountWon: w.amountWon || w.amount || 0, runs: w.run !== undefined ? [w.run] : [] }); continue; }
    cur.amount = Math.round((cur.amount + (w.amount || 0)) * 100) / 100;
    cur.amountWon = cur.amount;
    if (w.run !== undefined) cur.runs.push(w.run);
    if ((w.amount || 0) > (cur.bestShare || 0)) { cur.hand = w.hand; cur.handDescription = w.handDescription; }
    cur.bestShare = Math.max(cur.bestShare || 0, w.amount || 0);
  }
  return Array.from(byId.values());
};

// ============================================================================
//  MEMOIZED ANIMATION COMPONENT - Prevents render spam

// ============================================================================
const MemoizedAnimation = React.memo(({
  animation,
  senderPosition,
  targetPosition,
  onImpact,
}: {
  animation: ActiveAnimation;
  senderPosition: { x: number; y: number } | undefined;
  targetPosition: { x: number; y: number } | undefined;
  onImpact?: (targetId: string) => void;
}) => {
  return (
    <PlayerActionAnimation
      animation={animation}
      senderPosition={senderPosition}
      targetPosition={targetPosition}
      onImpact={onImpact}
    />
  );
}, (prevProps, nextProps) => {
  // Only re-render if animation ID changes or positions change significantly
  if (prevProps.animation.id !== nextProps.animation.id) return false;
  
  const prevSender = prevProps.senderPosition;
  const nextSender = nextProps.senderPosition;
  const prevTarget = prevProps.targetPosition;
  const nextTarget = nextProps.targetPosition;
  
  // Check if positions changed significantly (more than 5px)
  const senderMoved = !prevSender || !nextSender || 
    Math.abs(prevSender.x - nextSender.x) > 5 || 
    Math.abs(prevSender.y - nextSender.y) > 5;
  const targetMoved = !prevTarget || !nextTarget || 
    Math.abs(prevTarget.x - nextTarget.x) > 5 || 
    Math.abs(prevTarget.y - nextTarget.y) > 5;
  
  // Return true to skip re-render if positions haven't changed significantly
  return !senderMoved && !targetMoved;
});

interface PokerTableProps {
  sessionMode?: boolean; // home game: free join, host banks chips
  gameState: GameState;
  tableId: string;
  currentUserId: string;
  usernames: { [userId: string]: string };
  userPhotos?: { [userId: string]: string | undefined };
  onReload?: () => void;
  onSeatClick?: (seatIndex: number) => void;
  voiceEnabled?: boolean;
  soundEnabled?: boolean;  //  NEW: Separate sound control

  //  NEW: For Rules Modal

  specialRules?: SpecialRules;
  tableName?: string;
  //  NEW: Dynamic seat count

  maxSeats?: number;
  // Tournament felt info lines drawn inside the table SVG
  centerInfo?: string[];
  // Tournament chip leaders (top 3 ids, best first) - medal badges
  leaderBadges?: string[];
  onPlayerClick?: (playerId: string, playerName: string) => void;
  seatTags?: { [playerId: string]: string };
}

const PokerTable: React.FC<PokerTableProps> = ({
  sessionMode = false,
  gameState,
  tableId,
  currentUserId,
  usernames,
  userPhotos = {},
  onReload,
  onSeatClick,
  voiceEnabled = true,
  soundEnabled = true,  //  NEW: Default to true

  specialRules,
  tableName,
  maxSeats = 8,
  centerInfo,
  leaderBadges,
  onPlayerClick,
  seatTags,
}) => {
  const [raiseAmount, setRaiseAmount] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Warn before folding when checking is free (BB facing no raise etc.)
  const [confirmFreeFold, setConfirmFreeFold] = useState<boolean>(false);
  
  //  Ref to always have the latest gameState (avoids stale closure issues)

  const gameStateRef = useRef(gameState);
  useEffect(() => {
    gameStateRef.current = gameState;
    //  DEBUG: Log pot AND communityCards

    console.log('💰 [POKER_TABLE] gameState.pot:', gameState?.pot, '| round:', gameState?.round, '| communityCards:', JSON.stringify(gameState?.communityCards));
  }, [gameState]);
  
  //  Sync voice announcer with voiceEnabled prop

  useEffect(() => {
    voiceAnnouncer.setEnabled(voiceEnabled);
  }, [voiceEnabled]);

  //  Track previous round for voice announcements

  const prevRoundRef = useRef<string | null>(null);
  const prevWinnerRef = useRef<string | null>(null);
  
  //  Auto-clear action errors after 3 seconds

  useEffect(() => {
    if (actionError) {
      const timer = setTimeout(() => setActionError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionError]);
  const [showRaiseSlider, setShowRaiseSlider] = useState(false);
  
  //  RUN IT TWICE state

  const [showRITModal, setShowRITModal] = useState(false);
  const [ritResponding, setRitResponding] = useState(false);
  const ritOfferRef = useRef<string | null>(null); // Track which offer we've seen
  
  //  STRADDLE state

  const [canStraddle, setCanStraddle] = useState(false);
  const [straddlePosted, setStraddlePosted] = useState(false);
  const [postingStraddle, setPostingStraddle] = useState(false);
  
  //  BOMB POT announcement state

  const [showBombPotAnnouncement, setShowBombPotAnnouncement] = useState(false);
  const lastBombPotHandRef = useRef<number | null>(null);
  
  // Winner & notification states
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState<any>(null);
  const [lastBustNotificationId, setLastBustNotificationId] = useState<string | null>(null);
  const [lastWinnerNotificationId, setLastWinnerNotificationId] = useState<string | null>(null);

  //  Round announcement - show briefly at start of each round

  const [showRoundAnnouncement, setShowRoundAnnouncement] = useState(false);

  //  NEW: Show Cards state for fold wins

  const [showCardsChosen, setShowCardsChosen] = useState(false);
  const [showCardsTimer, setShowCardsTimer] = useState(5);
  const [showCardsExpired, setShowCardsExpired] = useState(false);

  //  Chat state

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  //  Winner Celebration state

  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAmount, setCelebrationAmount] = useState(0);
  const [celebrationHandDesc, setCelebrationHandDesc] = useState<string | undefined>();
  const [celebrationLoserDesc, setCelebrationLoserDesc] = useState<string | undefined>();
  const [celebrationIsFoldWin, setCelebrationIsFoldWin] = useState(false);
  const [lastCelebrationHandId, setLastCelebrationHandId] = useState<string | null>(null);

  //  STORIES: Share Win Modal state

  const [showShareWinModal, setShowShareWinModal] = useState(false);
  const [shareWinData, setShareWinData] = useState<{
    potAmount: number;
    handType?: string;
    opponents?: { odD: string; username: string; hand?: string; folded?: boolean }[];
    wasShowdown?: boolean;
  } | null>(null);
  const [showShareWinButton, setShowShareWinButton] = useState(false);

  //  GAME ITEMS STATE

  const [showItemsMenu, setShowItemsMenu] = useState(false);
  const [pkCashBalance, setPkCashBalance] = useState(0);
  const [activeAnimations, setActiveAnimations] = useState<ActiveAnimation[]>([]);
  // 🔊 iOS/Android audio unlock: mobile browsers block programmatic audio
  // until the page has seen a user gesture. Observers of a thrown item
  // made no gesture at impact time - prime a silent play on first touch
  // so item sounds work for everyone.
  useEffect(() => {
    const unlock = () => {
      try {
        const a = new Audio();
        a.muted = true;
        a.play().catch(() => {/* noop */});
      } catch { /* noop */ }
      window.removeEventListener('touchend', unlock);
      window.removeEventListener('pointerdown', unlock);
    };
    window.addEventListener('touchend', unlock, { once: true });
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => {
      window.removeEventListener('touchend', unlock);
      window.removeEventListener('pointerdown', unlock);
    };
  }, []);
  // Avatar hit reaction: set when a thrown item LANDS on a player, cleared
  // shortly after - drives the seat shake in the SVG.
  const [hitPlayerId, setHitPlayerId] = useState<string | null>(null);
  const hitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [playerPositions, setPlayerPositions] = useState<{ [id: string]: { x: number; y: number } }>({});

  //  INVITE MODAL STATE

  const [showInviteModal, setShowInviteModal] = useState(false);

  //  RULES MODAL STATE

  const [showRulesModal, setShowRulesModal] = useState(false);

  //  REBUY MODAL STATE

  const [showRebuyModal, setShowRebuyModal] = useState(false);
  const [isRebuyProcessing, setIsRebuyProcessing] = useState(false);

  //  LUCKY POT STATE

  const [showLuckyPot, setShowLuckyPot] = useState(false);
  const [luckyPotData, setLuckyPotData] = useState<any>(null);
  const lastLuckyPotHandRef = useRef<number | null>(null);
  const luckyPotHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ============================================================================
  // ⏱ TURN TIMER STATE (30 seconds standard)

  // ============================================================================
  const TURN_DURATION = 30;
  const [turnTimeLeft, setTurnTimeLeft] = useState<number>(TURN_DURATION);
  const [timeBankUsed, setTimeBankUsed] = useState(false);
  const turnTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastHandForTimerRef = useRef<number | null>(null);

  // ============================================================================
  // BOT CHAT TOAST (ported from the premium table) - shows the latest house
  // bot chat line as a corner toast so bot table-talk is visible without
  // opening the chat drawer.
  // ============================================================================
  const [botChatBubble, setBotChatBubble] = useState<string | null>(null);
  const [botChatBubbleName, setBotChatBubbleName] = useState<string>('');
  const botChatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastBotMessageIdRef = useRef<string | null>(null);

  // ============================================================================
  //  ALL-IN RUNOUT SUSPENSE STATE

  // ============================================================================
  type RunoutPhase = 'none' | 'reveal-cards' | 'show-equity' | 'deal-turn' | 'deal-river' | 'complete';
  const [runoutPhase, setRunoutPhase] = useState<RunoutPhase>('none');
  const [revealedPlayerIndex, setRevealedPlayerIndex] = useState<number>(-1);
  const [revealedCardIndex, setRevealedCardIndex] = useState<number>(-1);
  const [visibleBoardCount, setVisibleBoardCount] = useState<number>(0);
  const [playerEquities, setPlayerEquities] = useState<{ [playerId: string]: number }>({});
  //  Cache chips at start of runout to avoid spoiling winner

  const [cachedChips, setCachedChips] = useState<{ [playerId: string]: number }>({});
  
  //  RUN IT TWICE SUSPENSE STATE

  // ============================================================================
  type RitPhase = 'none' | 'reveal-cards' | 'equity-before' | 'run1-flop' | 'run1-turn' | 'run1-river' | 'run1-winner' | 'run2-flop' | 'run2-turn' | 'run2-river' | 'run2-winner' | 'complete';
  const [ritPhase, setRitPhase] = useState<RitPhase>('none');
  const [ritRun1VisibleCards, setRitRun1VisibleCards] = useState<number>(0);
  const [ritRun2VisibleCards, setRitRun2VisibleCards] = useState<number>(0);
  const [ritRun1Equity, setRitRun1Equity] = useState<{ [playerId: string]: number }>({});
  const [ritRun2Equity, setRitRun2Equity] = useState<{ [playerId: string]: number }>({});
  const [ritShowRun1Winner, setRitShowRun1Winner] = useState<boolean>(false);
  const [ritShowRun2Winner, setRitShowRun2Winner] = useState<boolean>(false);
  const ritTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRitHandRef = useRef<number | null>(null);
  const ritActiveRef = useRef<boolean>(false);
  //  ENTERPRISE: Capture RIT players at animation start to avoid stale closures

  const ritPlayersRef = useRef<any[]>([]);
  //  ENTERPRISE: Track animation start time for recovery timeout

  const ritAnimationStartRef = useRef<number>(0);
  //  Cache pot at start of runout to show during animation

  const [cachedPot, setCachedPot] = useState<number>(0);
  const runoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRunoutHandRef = useRef<number | null>(null);
  //  CRITICAL: Synchronous ref to avoid race condition with state

  const runoutActiveRef = useRef<boolean>(false);
  //  ENTERPRISE: Track animation start time for recovery timeout

  const runoutAnimationStartRef = useRef<number>(0);
  //  ENTERPRISE: Animation recovery timeout (30 seconds max)

  const ANIMATION_RECOVERY_TIMEOUT = 45000; // a preflop run-it-twice reveal runs close to 30s
  
  // ⏰ Next hand countdown
  const [nextHandCountdown, setNextHandCountdown] = useState<number>(5);

  // 🐰 Rabbit hunt - see the would-be runout after an early hand end.
  // Stores the hand number captured at click time so the auto-dealt next
  // hand (tournaments deal ~5s later) can't swap the fetch mid-view.
  const [rabbitHandNumber, setRabbitHandNumber] = useState<number | null>(null);

  // ⚡ PRE-ACTIONS: arm fold/check/call-any while waiting for the turn;
  // the armed action fires automatically the instant the turn arrives.
  const [preAction, setPreAction] = useState<'checkFold' | 'check' | 'callAny' | null>(null);

  //  SECURE: Fetch current user's hole cards from secure endpoint

  // Backend hides all cards in gameState, we fetch our own via /getMyCards
  const [myHoleCards, setMyHoleCards] = useState<{rank: string; suit: string}[]>([]);
  const lastFetchedHandRef = useRef<number | null>(null);

  //  SAFETY: Filter out any null/invalid players

  //  CRITICAL: Use gameState as dependency, not just gameState.players

  // This ensures re-computation when any part of gameState changes
  //  SECURE: Merge myHoleCards for current user (fetched from secure endpoint)

  const safePlayers = useMemo(() => {
    if (!gameState || !gameState.players || !Array.isArray(gameState.players)) {
      return [];
    }
    const players = gameState.players;
    const filtered = players.filter((p: any) => {
      return p && 
             typeof p === 'object' && 
             p.id && 
             typeof p.id === 'string' &&
             p.seat !== undefined;
    });
    
    //  Merge securely fetched hole cards for current user

    return filtered.map(p => {
      if (p.id === currentUserId && myHoleCards.length === 2) {
        return { ...p, holeCards: myHoleCards };
      }
      return p;
    });
  }, [gameState?.players, currentUserId, myHoleCards]);
  
  //  Safe current player lookup

  const currentPlayer = safePlayers.find(p => p?.id === currentUserId) || null;
  const currentPlayerIndex = currentPlayer ? safePlayers.findIndex(p => p?.id === currentUserId) : -1;
  const isCurrentPlayerTurn = currentPlayerIndex >= 0 && gameState?.currentPlayerIndex === currentPlayerIndex;

  //  ENTERPRISE: Comprehensive guard for action validity

  const canTakeAction = useMemo(() => {
    // Must be current player's turn
    if (!isCurrentPlayerTurn) return false;
    
    // Game must be actively playing
    if (gameState.status !== 'playing') return false;
    
    // Round must be active (not ended/waiting/showdown)
    if (!gameState.round || gameState.round === 'ended' || gameState.round === 'waiting' || gameState.round === 'showdown') return false;
    
    // Current player must exist and not be folded/all-in
    if (!currentPlayer) return false;
    if (currentPlayer.folded || currentPlayer.allIn || currentPlayer.eliminated) return false;
    
    // Not waiting for next hand or needing rebuy
    if (currentPlayer.waitingForNextHand || currentPlayer.needsRebuy) return false;
    
    return true;
  }, [isCurrentPlayerTurn, gameState.status, gameState.round, currentPlayer]);

  //  SAFE: Get current turn player with null protection

  const currentTurnPlayer = useMemo(() => {
    if (!gameState || gameState.currentPlayerIndex < 0) return null;
    if (gameState.currentPlayerIndex >= safePlayers.length) return null;
    const player = safePlayers[gameState.currentPlayerIndex];
    return player && player.id ? player : null;
  }, [gameState, safePlayers, gameState?.currentPlayerIndex]);

  //  Computed: Is runout in progress? (for JSX rendering)

  const isRunoutInProgress = runoutPhase !== 'none' && runoutPhase !== 'complete';

  //  PRODUCTION: Master cleanup on unmount - prevents timer leaks on fast table switch

  useEffect(() => {
    return () => {
      console.log('🧹 [CLEANUP] PokerTable unmounting - clearing all timers');
      if (turnTimerRef.current) {
        clearInterval(turnTimerRef.current);
        turnTimerRef.current = null;
      }
      if (ritTimerRef.current) {
        clearTimeout(ritTimerRef.current);
        ritTimerRef.current = null;
      }
      if (runoutTimerRef.current) {
        clearTimeout(runoutTimerRef.current);
        runoutTimerRef.current = null;
      }
    };
  }, []);

  //  FIX: Cache pot when it's non-zero (before backend zeroes it)

  useEffect(() => {
    if (gameState.pot > 0 && runoutPhase === 'none') {
      setCachedPot(Math.round(gameState.pot * 100) / 100);
    }
  }, [gameState.pot, runoutPhase]);

  // BOT CHAT TOAST subscription - mirrors the premium table. Only active
  // when a house bot is seated; auto-hides each message after 6s.
  useEffect(() => {
    if (!tableId) return;
    const hasBotPlayer = safePlayers.some((p: any) => p?.id?.startsWith('house_bot_'));
    if (!hasBotPlayer) return;

    const unsubscribe = subscribeToMessages(tableId, (messages: ChatMessage[]) => {
      if (messages.length === 0) return;
      const latestBotMsg = [...messages].reverse().find(
        (m) => m.senderId?.startsWith('house_bot_') || (m as any).isHouseBot
      );
      if (!latestBotMsg || latestBotMsg.id === lastBotMessageIdRef.current) return;
      lastBotMessageIdRef.current = latestBotMsg.id;
      setBotChatBubble(stripEmoji(latestBotMsg.message || ''));
      setBotChatBubbleName(latestBotMsg.senderName || 'House Bot');
      if (botChatTimerRef.current) clearTimeout(botChatTimerRef.current);
      botChatTimerRef.current = setTimeout(() => setBotChatBubble(null), 4500);
    }, 10);

    return () => {
      unsubscribe();
      if (botChatTimerRef.current) clearTimeout(botChatTimerRef.current);
    };
  }, [tableId, safePlayers]);

  //  Detect if this is a fold win (all others folded)

  const isFoldWin = useMemo(() => {
    if (!gameState.handResult?.allOthersFolded) return false;
    return true;
  }, [gameState.handResult]);

  const isCurrentUserWinner = useMemo(() => {
    return gameState.winners?.some(w => w.playerId === currentUserId) || false;
  }, [gameState.winners, currentUserId]);

  const canShowCards = isFoldWin && isCurrentUserWinner && !showCardsChosen && !showCardsExpired && !gameState.handResult?.winnerShowedCards;

  //  Reset show cards state when hand changes

  useEffect(() => {
    if (gameState.handNumber) {
      setShowCardsChosen(false);
      setShowCardsExpired(false);
      setShowCardsTimer(5);
      //  Clear notifications from previous hand when new hand starts

      if (gameState.round === 'preflop') {
        setShowNotification(false);
        setNotificationData(null);
      }
    }
  }, [gameState.handNumber, gameState.round]);

  //  Show cards countdown timer

  useEffect(() => {
    if (!canShowCards) return;
    
    const timer = setInterval(() => {
      setShowCardsTimer(prev => {
        if (prev <= 1) {
          setShowCardsExpired(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [canShowCards]);

  // ============================================================================
  // ⏱ TURN TIMER - 30 second countdown with auto-fold

  //  PRODUCTION: Uses server-authoritative turnStartedAt timestamp

  // ============================================================================
  useEffect(() => {
    // Reset time bank for new hand
    if (gameState.handNumber !== lastHandForTimerRef.current) {
      lastHandForTimerRef.current = gameState.handNumber;
      setTimeBankUsed(false);
      setTurnTimeLeft(TURN_DURATION);
      console.log('⏱️ [TIMER] Reset for new hand');
    }

    // Only run timer when game is active and it's someone's turn
    if (gameState.status !== 'playing' || gameState.round === 'ended' || gameState.round === 'showdown') {
      if (turnTimerRef.current) {
        clearInterval(turnTimerRef.current);
        turnTimerRef.current = null;
      }
      return;
    }

    const turnPlayer = safePlayers[gameState?.currentPlayerIndex];
    if (!turnPlayer || !turnPlayer.id || turnPlayer.folded || turnPlayer.allIn) {
      return;
    }

    //  PRODUCTION: Calculate remaining time from server timestamp (with fallback)

    let initialTime = TURN_DURATION;
    if (gameState.turnStartedAt) {
      const elapsed = Math.floor((Date.now() - gameState.turnStartedAt) / 1000);
      initialTime = Math.max(0, TURN_DURATION - elapsed);
      console.log(`⏱️ [TIMER] Server turnStartedAt: elapsed=${elapsed}s, remaining=${initialTime}s`);
    }
    
    setTurnTimeLeft(initialTime);
    console.log(`⏱️ [TIMER] Turn changed to ${turnPlayer.name || turnPlayer.id.substring(0,8)} - ${initialTime}s remaining`);

    // Clear any existing timer
    if (turnTimerRef.current) {
      clearInterval(turnTimerRef.current);
    }

    // Start countdown
    turnTimerRef.current = setInterval(() => {
      setTurnTimeLeft(prev => {
        if (prev <= 0) return 0; //  Prevent going negative

        const newTime = prev - 1;
        
        // Log every 5 seconds and when below 10
        if (newTime % 5 === 0 || newTime <= 10) {
          console.log(`⏱️ [TIMER] ${newTime}s remaining`);
        }
        
        // Auto-fold when timer hits 0 (only if it's our turn)
        if (newTime <= 0 && isCurrentPlayerTurn && currentPlayer && !currentPlayer.folded) {
          console.log('⏱️ [TIMER] Time expired - auto-folding via server (timeout tracked for away mode)');
          //  FIX #4: Call server endpoint directly, not DOM click

          // ⏰ AWAY MODE: Use timeoutFold to track consecutive timeouts
          gameApiService.timeoutFold(tableId).catch(err => {
            console.error('⏱️ [TIMER] Auto-fold failed:', err);
          });
          if (turnTimerRef.current) {
            clearInterval(turnTimerRef.current);
            turnTimerRef.current = null;
          }
          return 0;
        }
        
        if (newTime < 0) return 0; //  Prevent negative timer

        return Math.max(0, newTime);
      });
    }, 1000);

    return () => {
      if (turnTimerRef.current) {
        clearInterval(turnTimerRef.current);
        turnTimerRef.current = null;
      }
    };
  }, [gameState.currentPlayerIndex, gameState.status, gameState.round, gameState.handNumber, gameState.turnStartedAt]);

  //  ENTERPRISE: Detect stuck game state (player left mid-turn)

  useEffect(() => {
    // Only check when game is supposedly playing but might be stuck
    if (gameState.status !== 'playing' || gameState.round === 'ended' || gameState.round === 'waiting') return;

    const currentTurn = safePlayers[gameState?.currentPlayerIndex];

    // If current turn player doesn't exist, folded, or left, game might be stuck
    if (!currentTurn || currentTurn.folded || currentTurn.leavingTable || currentTurn.eliminated) {
      console.log(`⚠️ [STUCK_CHECK] Current turn player invalid - may need recovery`);

      // After 5s of confirmed stuck, ping /kickStalledTurn so the server
      // either advances the turn, fold-wins the pot to the lone survivor,
      // or otherwise unwedges the hand. Without this the hand sits forever
      // and the next-hand timer eventually wipes the pot - which is what
      // happened when an all-in shove was followed by all bots folding but
      // currentPlayerIndex stayed stuck on a folded seat.
      const stuckTimer = setTimeout(() => {
        const latestState = gameStateRef.current;
        if (latestState.status === 'playing' && latestState.round !== 'ended' && latestState.round !== 'waiting') {
          const latestTurn = latestState.players?.[latestState.currentPlayerIndex];
          if (!latestTurn || latestTurn.folded || latestTurn.leavingTable) {
            console.error(`🚨 [STUCK_CHECK] Game appears stuck - pinging /kickStalledTurn for recovery`);
            try {
              const tableId = (gameState as any)?.tableId
                || (window as any).__currentTableId
                || (document.location.pathname.match(/\/game\/([^/?#]+)/) || [])[1];
              if (tableId) {
                // Fire-and-forget; the server validates server-side and
                // either advances the turn or settles the hand.
                import('../services/gameApiService').then(m => {
                  m.default.kickStalledTurn(tableId).catch(() => {/* best-effort */});
                });
              }
            } catch {/* noop */}
          }
        }
      }, 5000);

      return () => clearTimeout(stuckTimer);
    }
  }, [gameState, gameState.status, gameState.round, gameState.currentPlayerIndex, safePlayers]);

  // 🤖 BOT STALL WATCHDOG: a healthy bot acts within a few seconds. If a
  // bot has held the turn with NO state change for ~9s, ping the server
  // kick (which soft-kicks the bot processor, then force-folds at 25s of
  // total inactivity). Any table write resets this via the deps, so it
  // only ever fires on a genuinely frozen bot - this is what turns
  // "table frozen for 30-40s" into "unstuck in under 10s".
  useEffect(() => {
    if (gameState.status !== 'playing') return;
    if (!gameState.round || gameState.round === 'ended' || gameState.round === 'waiting' || gameState.round === 'showdown') return;
    if (gameState.allInShowdown || gameState.showdownComplete) return;
    const turnPlayer = safePlayers[gameState.currentPlayerIndex];
    // Server-driven seats: bots AND sat-out players (the sit-out fast path
    // auto-acts them, but a missed trigger has no retry - the kick is the
    // backup that keeps their turn from hanging).
    const serverDriven = !!turnPlayer?.id
      && (String(turnPlayer.id).startsWith('house_bot_') || (turnPlayer as any).isAway === true);
    if (!serverDriven) return;
    let iv: ReturnType<typeof setInterval> | null = null;
    const ping = () => {
      console.log(`🤖 [BOT_WATCHDOG] ${String(turnPlayer.id).slice(-6)} inactive - pinging kickStalledTurn`);
      gameApiService.kickStalledTurn(tableId).catch(() => {});
    };
    const first = setTimeout(() => { ping(); iv = setInterval(ping, 6000); }, 9000);
    return () => { clearTimeout(first); if (iv) clearInterval(iv); };
  }, [gameState.currentPlayerIndex, gameState.stateVersion, gameState.turnStartedAt,
      gameState.handNumber, gameState.status, gameState.round, tableId, safePlayers]);

  // +10 Second Time Bank handler
  const handleUseTimeBank = useCallback(() => {
    if (timeBankUsed || !isCurrentPlayerTurn) return;
    
    console.log('⏱️ [TIME BANK] +10 seconds used!');
    setTimeBankUsed(true);
    setTurnTimeLeft(prev => Math.min(prev + 10, TURN_DURATION + 10));
    playActionSound('check', soundEnabled);
  }, [timeBankUsed, isCurrentPlayerTurn, soundEnabled]);

  // ============================================================================
  //  ALL-IN RUNOUT SUSPENSE - Detect and animate

  // ============================================================================
  
  // Detect all-in showdown situation
  //  FIXED: Only trigger suspense runout if someone is actually ALL-IN

  // Check-downs (everyone just checks to showdown) should NOT have runout suspense
  const allInShowdownPlayers = useMemo(() => {
    // Only check when we just entered showdown with community cards
    if (gameState.round !== 'showdown' && gameState.round !== 'ended') return [];
    
    if (gameState.handResult?.allOthersFolded) {
      return []; // Fold win, no runout
    }
    
    // Only filter out folded players
    const nonFolded = safePlayers.filter(p => inShowdownHand(p));
    
    //  FIX #1: Use gameState.allInOccurred (set immediately when anyone goes all-in)

    // OR gameState.allInShowdown (set when runout starts)
    // NEVER check player.allIn - it gets cleared by backend!
    const hasAllInOccurred = gameState.allInOccurred === true || gameState.allInShowdown === true;
    
    if (!hasAllInOccurred) {
      console.log('🎬 [SHOWDOWN] Check-down (no all-ins) - skipping runout suspense');
      return []; // No suspense for check-downs
    }
    
    console.log('🎬 [SHOWDOWN] All-in detected! Non-folded players:', nonFolded.length, 
      'Hole cards available:', nonFolded.filter(p => p.holeCards?.length === 2).length,
      'allInOccurred:', gameState.allInOccurred, 'allInShowdown:', gameState.allInShowdown);
    
    return nonFolded;
  }, [gameState.round, safePlayers, gameState.handResult, gameState.allInShowdown, gameState.allInOccurred]);

  // Calculate equity when we have hole cards visible
  //  FIXED: Accept boardCount as parameter to avoid stale state

  const calculateEquities = useCallback(async (boardCount: number) => {
    console.log('📊 [EQUITY] calculateEquities called with boardCount:', boardCount);
    
    if (allInShowdownPlayers.length < 2) {
      console.log('📊 [EQUITY] Not enough players:', allInShowdownPlayers.length);
      return;
    }
    
    // Only calculate for players who have valid (revealed) hole cards
    //  Hidden cards have rank='' which would break equity calculation

    const playersWithCards = allInShowdownPlayers.filter(p => 
      p.holeCards?.length === 2 && 
      p.holeCards[0]?.rank && p.holeCards[0].rank !== '' &&
      p.holeCards[1]?.rank && p.holeCards[1].rank !== ''
    );
    if (playersWithCards.length < 2) {
      console.log('📊 [EQUITY] Not enough players with valid hole cards:', playersWithCards.length);
      return;
    }
    
    // Get board cards directly from gameState
    const boardToUse = gameState.communityCards.slice(0, boardCount);
    console.log('📊 [EQUITY] Board cards:', boardToUse.length, boardToUse.map(c => `${c.rank}${c.suit[0]}`));
    
    try {
      const { calculateEquity } = await import('../services/equityCalculator');
      
      const playersData = playersWithCards.map(p => ({
        id: p.id,
        holeCards: p.holeCards!,
      }));

      const results = calculateEquity(playersData, boardToUse, 1000);
      
      const equityMap: { [playerId: string]: number } = {};
      for (const r of results) {
        equityMap[r.playerId] = r.equity;
      }
      
      console.log(`📊 [EQUITY] Results for ${boardCount} cards:`, equityMap);
      setPlayerEquities(equityMap);
    } catch (err) {
      console.error('📊 [EQUITY] Failed to calculate:', err);
    }
  }, [allInShowdownPlayers, gameState.communityCards]);

  //  RUN IT TWICE: Calculate equity for a specific board

  const calculateRitEquity = useCallback(async (
    boardCards: any[], 
    players: any[],
    setEquityFn: (eq: { [playerId: string]: number }) => void
  ) => {
    console.log('📊 [RIT EQUITY] Calculating for board:', boardCards.length, 'cards');
    
    //  Only calculate for players with valid (revealed) hole cards

    const playersWithCards = players.filter(p => 
      p.holeCards?.length === 2 && 
      p.holeCards[0]?.rank && p.holeCards[0].rank !== '' &&
      p.holeCards[1]?.rank && p.holeCards[1].rank !== ''
    );
    if (playersWithCards.length < 2) {
      console.log('📊 [RIT EQUITY] Not enough players with valid cards');
      return;
    }
    
    try {
      const { calculateEquity } = await import('../services/equityCalculator');
      
      const playersData = playersWithCards.map(p => ({
        id: p.id,
        holeCards: p.holeCards!,
      }));

      const results = calculateEquity(playersData, boardCards, 1000);
      
      const equityMap: { [playerId: string]: number } = {};
      for (const r of results) {
        equityMap[r.playerId] = r.equity;
      }
      
      console.log('📊 [RIT EQUITY] Results:', equityMap);
      setEquityFn(equityMap);
    } catch (err) {
      console.error('📊 [RIT EQUITY] Failed:', err);
    }
  }, []);

  //  RUN IT TWICE: Detect when RIT offer is made

  useEffect(() => {
    const ritOffer = gameState.runItTwiceOffer;
    if (ritOffer && !ritOffer.allResponded && !ritOffer.declined) {
      // Check if current user is part of the RIT offer
      const isInOffer = ritOffer.players?.includes(currentUserId);
      const hasResponded = ritOffer.responses?.[currentUserId] !== undefined;
      
      // Show modal if user is in offer and hasn't responded yet. The offer is
      // identified by the hand and the moment it was made: keying it on the
      // player list alone meant a second all-in against the same opponent in
      // one session never showed the prompt, and the table sat frozen.
      const offerKey = `${gameState.handNumber || 0}:${ritOffer.offeredAt || 0}:${(ritOffer.players || []).join(',')}`;
      if (isInOffer && !hasResponded && ritOfferRef.current !== offerKey) {
        ritOfferRef.current = offerKey;
        setShowRITModal(true);
        console.log('🔄 [RIT] Showing Run It Twice modal');
      } else if (isInOffer && !hasResponded && !showRITModal) {
        // Same offer, modal somehow closed (rerender after a reload): show it
        setShowRITModal(true);
      }
    } else {
      setShowRITModal(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.runItTwiceOffer, gameState.handNumber, currentUserId]);

  //  STRADDLE: Check if current player can post straddle

  useEffect(() => {
    // Can only straddle between hands when waiting for game to start
    const isWaiting = gameState.status !== 'playing' || gameState.round === 'ended';
    const straddleEnabled = specialRules?.straddleEnabled === true;
    const currentPlayer = gameState.players?.find((p: any) => p?.id === currentUserId);
    const hasChips = currentPlayer && currentPlayer.chips >= (gameState.bigBlind || 2) * 2;
    const noStraddlePending = !gameState.hasStraddle && !straddlePosted;
    
    setCanStraddle(isWaiting && straddleEnabled && hasChips && noStraddlePending);
  }, [gameState.status, gameState.round, specialRules?.straddleEnabled, currentUserId, gameState.players, gameState.bigBlind, gameState.hasStraddle, straddlePosted]);

  // Reset straddle state on new hand
  useEffect(() => {
    if (gameState.handNumber) {
      setStraddlePosted(false);
    }
  }, [gameState.handNumber]);

  //  LUCKY POT: Detect and display lucky pot multiplier

  useEffect(() => {
    // Only trigger when round is ended and there's a lucky pot result
    if (gameState.round !== 'ended' || !gameState.luckyPotResult?.triggered) {
      return;
    }
    // Lucky Pot is a cash-game feature - NEVER show it on tournament
    // tables, whatever the server state claims.
    if ((gameState as any).tournamentId || (gameState as any).isTournament
      || (gameState as any).tournamentTableNumber != null) {
      return;
    }
    
    // Prevent duplicate triggers for same hand
    if (gameState.handNumber === lastLuckyPotHandRef.current) {
      return;
    }
    
    lastLuckyPotHandRef.current = gameState.handNumber;
    setLuckyPotData(gameState.luckyPotResult);
    setShowLuckyPot(true);

    // Play jackpot sound
    playActionSound('jackpot', soundEnabled);

    // Auto-hide based on tier
    const displayTime = gameState.luckyPotResult.tier === 'mega' ? 4000 :
                        gameState.luckyPotResult.tier === 'super' ? 3000 : 2000;

    // Timer lives in a ref, NOT in the effect cleanup. luckyPotResult gets a
    // new object identity on every Firestore snapshot, so this effect re-runs
    // constantly; the old `return () => clearTimeout(timer)` cancelled the
    // hide on the very next snapshot and the banner sat over the next hand -
    // covering the player's own cards on their own turn - forever.
    if (luckyPotHideTimerRef.current) clearTimeout(luckyPotHideTimerRef.current);
    luckyPotHideTimerRef.current = setTimeout(() => {
      setShowLuckyPot(false);
      luckyPotHideTimerRef.current = null;
    }, displayTime);
  }, [gameState.round, gameState.luckyPotResult, gameState.handNumber, soundEnabled]);

  //  SECURE: Fetch current user's hole cards when hand starts

  useEffect(() => {
    // Only fetch when hand number changes and we're in a hand
    if (!gameState.handNumber || !currentUserId || !tableId) return;
    if (gameState.handNumber === lastFetchedHandRef.current) return;
    if (gameState.round === 'ended' || gameState.status === 'ended') return;
    
    lastFetchedHandRef.current = gameState.handNumber;

    // NEW HAND: drop the previous hand's cards IMMEDIATELY. The delayed
    // between-hands clear gets cancelled when the next hand arrives inside
    // its window, so stale cards were rendering into the new hand and then
    // visibly 'changing' when the secure fetch landed. Card backs show
    // until the real cards arrive - reads as the deal, never as a swap.
    setMyHoleCards([]);

    // Fetch our cards from secure endpoint
    const fetchMyCards = async () => {
      try {
        const result = await gameApiService.getMyCards(tableId);
        if (result.success && result.holeCards && result.holeCards.length === 2) {
          console.log('🃏 [MY_CARDS] Fetched:', result.holeCards.map(c => `${c.rank}${c.suit[0]}`).join(' '));
          setMyHoleCards(result.holeCards);
        }
      } catch (error) {
        console.error('❌ [MY_CARDS] Failed to fetch:', error);
      }
    };
    
    fetchMyCards();
  }, [gameState.handNumber, gameState.round, gameState.status, currentUserId, tableId]);

  // Clear myHoleCards when between hands or waiting
  useEffect(() => {
    if (!gameState || gameState.status === 'waiting' || gameState.round === 'ended') {
      // Small delay before clearing to allow showdown display
      const timer = setTimeout(() => {
        setMyHoleCards([]);
        lastFetchedHandRef.current = null;
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [gameState?.status, gameState?.round]);

  //  ENTERPRISE: Animation Recovery Mechanism

  // Monitors for stuck animations and forces completion after timeout
  // This prevents games from getting stuck if animation logic fails
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const now = Date.now();
      
      // Check RIT animation
      if (ritActiveRef.current && ritPhase !== 'none' && ritPhase !== 'complete') {
        const elapsed = now - ritAnimationStartRef.current;
        if (elapsed > ANIMATION_RECOVERY_TIMEOUT) {
          console.error('🚨 [RIT RECOVERY] Animation stuck for', elapsed, 'ms - forcing completion');
          console.error('🚨 [RIT RECOVERY] Current phase:', ritPhase);
          setRitPhase('complete');
          ritActiveRef.current = false;
          if (ritTimerRef.current) {
            clearTimeout(ritTimerRef.current);
            ritTimerRef.current = null;
          }
        }
      }
      
      // Check standard runout animation
      if (runoutActiveRef.current && runoutPhase !== 'none' && runoutPhase !== 'complete') {
        const elapsed = now - runoutAnimationStartRef.current;
        if (elapsed > ANIMATION_RECOVERY_TIMEOUT) {
          console.error('🚨 [RUNOUT RECOVERY] Animation stuck for', elapsed, 'ms - forcing completion');
          console.error('🚨 [RUNOUT RECOVERY] Current phase:', runoutPhase);
          setRunoutPhase('complete');
          runoutActiveRef.current = false;
          if (runoutTimerRef.current) {
            clearTimeout(runoutTimerRef.current);
            runoutTimerRef.current = null;
          }
        }
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(checkInterval);
  }, [ritPhase, runoutPhase]);

  //  ENTERPRISE: RUN IT TWICE Animation System

  // - Captures players in ref at start to avoid stale closures
  // - Tracks animation start time for recovery timeout
  // - Does NOT depend on safePlayers to prevent cleanup killing animation
  // - Uses gameState.players directly for initial player detection
  // ── RUN IT TWICE TIMELINE (the premium table's approach, ported) ───────
  // One fixed schedule from the moment the results land: shared cards and
  // percentages, then each remaining street of run 1 with a pause, then its
  // winner locks in, then run 2 the same way. No phase machine, no
  // conditions that can skip it.
  const [ritRunsVisible, setRitRunsVisible] = useState(0);
  const [ritPartialCards, setRitPartialCards] = useState(0);
  const [ritLiveEquity, setRitLiveEquity] = useState<{ [id: string]: number }>({});
  const ritRuns: any[] | null = useMemo(() => {
    const rr: any = (gameState as any).runItTwiceResults;
    if (!rr) return null;
    if (Array.isArray(rr.runs) && rr.runs.length >= 2) return rr.runs;
    if (rr.run1 && rr.run2) return [rr.run1, rr.run2];
    return null;
  }, [(gameState as any).runItTwiceResults]);
  useEffect(() => {
    if (!ritRuns) { setRitRunsVisible(0); setRitPartialCards(0); setRitLiveEquity({}); return; }
    const b0 = ritRuns[0]?.communityCards || [];
    const b1 = ritRuns[1]?.communityCards || [];
    let shared = 0;
    while (shared < b0.length && shared < b1.length
      && b0[shared]?.rank === b1[shared]?.rank && b0[shared]?.suit === b1[shared]?.suit) shared++;
    const total = b0.length || 5;
    const hands = (gameState.players || [])
      .filter((p: any) => p && inShowdownHand(p) && Array.isArray(p.holeCards) && p.holeCards.length === 2 && p.holeCards.every((c: any) => c && c.rank))
      .map((p: any) => ({ id: p.id, holeCards: p.holeCards }));
    const runEquity = async (runIdx: number, cards: number) => {
      if (hands.length < 2) return;
      try {
        const { calculateEquity } = await import('../services/equityCalculator');
        const board = (ritRuns[runIdx]?.communityCards || []).slice(0, cards);
        const dead: any[] = [];
        for (let r = 0; r < runIdx; r++) dead.push(...(ritRuns[r]?.communityCards || []).slice(shared));
        const res = calculateEquity(hands as any, board, 600, dead);
        const map: { [id: string]: number } = {};
        res.forEach((r: any) => { map[r.playerId] = r.equity; });
        setRitLiveEquity(map);
      } catch { /* percentages are garnish; the reveal continues */ }
    };
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    let t = 600;
    setRitPhase('reveal-cards');
    ritActiveRef.current = true;
    ritAnimationStartRef.current = Date.now();
    lastRitHandRef.current = gameState.handNumber;
    setRitRunsVisible(0);
    setRitPartialCards(0);
    for (let r = 0; r < ritRuns.length; r++) {
      const rr = r;
      timers.push(setTimeout(() => { setRitRunsVisible(rr); setRitPartialCards(shared); runEquity(rr, shared); }, t));
      t += 1800;
      // Real poker: the flop lands as three cards, then the turn, then the
      // river, each with a pause and fresh percentages.
      const streets: number[] = [];
      if (shared < 3) streets.push(3);
      if (shared < 4) streets.push(4);
      if (shared < 5) streets.push(Math.min(5, total));
      for (const cc of streets) {
        timers.push(setTimeout(() => { setRitPartialCards(cc); runEquity(rr, cc); }, t));
        t += cc >= total ? 2600 : 2300; // extra beat on the river
      }
      timers.push(setTimeout(() => { setRitRunsVisible(rr + 1); setRitPartialCards(0); }, t));
      t += 1800;
    }
    timers.push(setTimeout(() => { setRitLiveEquity({}); setRitPhase('complete'); ritActiveRef.current = false; }, t));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!ritRuns, gameState.handNumber]);

  useEffect(() => {
    // Superseded by the timeline above - kept only so nothing else that
    // referenced this effect's state breaks; it never runs a sequence now.
    if (!gameState.runItTwiceResults || true) return;
    if (gameState.handNumber === lastRitHandRef.current) return;
    if (ritActiveRef.current) return;
    
    console.log('🔄 [RIT SUSPENSE] Starting Run It Twice animation');
    lastRitHandRef.current = gameState.handNumber;
    ritActiveRef.current = true;
    ritAnimationStartRef.current = Date.now(); //  Track start time for recovery

    
    //  ENTERPRISE: Get players directly from gameState.players

    // This avoids dependency on safePlayers which can change and kill the animation
    const gamePlayers = gameState.players || [];
    const ritPlayers = gamePlayers.filter((p: any) => 
      p && 
      inShowdownHand(p) && 
      p.holeCards?.length === 2 &&
      p.holeCards[0]?.rank && p.holeCards[0].rank !== ''
    );
    
    //  Store in ref so closure always has access to correct players

    ritPlayersRef.current = ritPlayers;
    
    const run1Cards = gameState.runItTwiceResults.run1?.communityCards || [];
    const run2Cards = gameState.runItTwiceResults.run2?.communityCards || [];
    
    // Get starting board count (cards that were already dealt before all-in)
    const previousRound = lastPlayingRoundRef.current;
    let startingBoardCount = 0;
    switch (previousRound) {
      case 'preflop': startingBoardCount = 0; break;
      case 'flop': startingBoardCount = 3; break;
      case 'turn': startingBoardCount = 4; break;
      case 'river': startingBoardCount = 5; break;
      default: startingBoardCount = 0;
    }
    
    console.log('🔄 [RIT] Starting from', startingBoardCount, 'cards, players:', ritPlayers.length);
    
    //  ENTERPRISE: Handle edge case where no valid players found

    // The boards are dealt with suspense NO MATTER what the seats show.
    // Fewer than two revealed hands only means the percentages are skipped
    // (equity needs both hands); completing instantly here is how a run
    // against a busted or hidden opponent flashed both finished boards.
    if (ritPlayers.length < 2) {
      console.warn('🔄 [RIT] Fewer than two revealed hands - dealing the boards without percentages');
    }
    
    // Reset state
    setRitPhase('reveal-cards');
    setRitRun1VisibleCards(startingBoardCount);
    setRitRun2VisibleCards(startingBoardCount); //  FIX: Run 2 also starts from same board

    setRitRun1Equity({});
    setRitRun2Equity({});
    setRitShowRun1Winner(false);
    setRitShowRun2Winner(false);
    setRevealedPlayerIndex(-1);
    setRevealedCardIndex(-1);
    
    // Clear any existing timer
    if (ritTimerRef.current) clearTimeout(ritTimerRef.current);
    
    //  FIX: Use SAME timing constants as normal runout for consistent suspense

    const CARD_REVEAL_DELAY = 600;      // 0.6s between each card flip (same as normal)
    const PLAYER_SWITCH_DELAY = 800;    // 0.8s pause between players (same as normal)
    const BOARD_CARD_DISPLAY = 2500;    // 2.5s to read each new board card + equity (same as normal)
    const RIVER_SUSPENSE = 2000;        // 2s dramatic pause on river before winner
    const WINNER_DISPLAY_TIME = 2500;   // 2.5s to show winner before moving to next run
    const RUN_TRANSITION_DELAY = 1500;  // 1.5s pause between Run 1 and Run 2
    
    // Track progress with local variables (not React state which has stale closures)
    let playerIdx = 0;
    let cardIdx = 0;
    let currentPhase = 'reveal-cards';
    let run1CardCount = startingBoardCount;
    let run2CardCount = startingBoardCount; //  FIX: Run 2 starts from same board

    
    //  ENTERPRISE: Use ref for players to avoid stale closure

    const getPlayers = () => ritPlayersRef.current;
    
    const runSequence = () => {
      const players = getPlayers();
      console.log('🔄 [RIT] Phase:', currentPhase, 'playerIdx:', playerIdx, 'cardIdx:', cardIdx, 'playersCount:', players.length);
      
      //  Safety check - if animation was reset, stop

      if (!ritActiveRef.current) {
        console.log('🔄 [RIT] Animation was reset, stopping sequence');
        return;
      }
      
      // Phase 1: Reveal hole cards one by one (same as normal runout)
      if (currentPhase === 'reveal-cards' && playerIdx < players.length) {
        if (cardIdx < 2) {
          setRevealedPlayerIndex(playerIdx);
          setRevealedCardIndex(cardIdx);
          console.log(`🔄 [RIT] Revealing player ${playerIdx} card ${cardIdx}`);
          cardIdx++;
          ritTimerRef.current = setTimeout(runSequence, CARD_REVEAL_DELAY);
        } else {
          playerIdx++;
          cardIdx = 0;
          ritTimerRef.current = setTimeout(runSequence, PLAYER_SWITCH_DELAY);
        }
        return;
      }
      
      // After hole cards revealed - show initial equity with starting board
      if (currentPhase === 'reveal-cards') {
        currentPhase = 'equity-before';
        setRitPhase('equity-before');
        const boardForEquity = run1Cards.slice(0, startingBoardCount);
        calculateRitEquity(boardForEquity, players, setRitRun1Equity);
        ritTimerRef.current = setTimeout(runSequence, BOARD_CARD_DISPLAY);
        return;
      }
      
      // Move to Run 1 sequence
      if (currentPhase === 'equity-before') {
        currentPhase = 'run1';
        run1CardCount = startingBoardCount;
      }
      
      // ============================================
      // RUN 1 - Same suspense pattern as normal runout
      // ============================================
      if (currentPhase === 'run1') {
        // Deal flop if needed (from preflop all-in)
        if (run1CardCount < 3) {
          console.log('🔄 [RIT Run1] Dealing flop');
          setRitPhase('run1-flop');
          run1CardCount = 3;
          setRitRun1VisibleCards(3);
          calculateRitEquity(run1Cards.slice(0, 3), players, setRitRun1Equity);
          ritTimerRef.current = setTimeout(runSequence, BOARD_CARD_DISPLAY);
          return;
        }
        // Deal turn
        if (run1CardCount < 4) {
          console.log('🔄 [RIT Run1] Dealing turn');
          setRitPhase('run1-turn');
          run1CardCount = 4;
          setRitRun1VisibleCards(4);
          calculateRitEquity(run1Cards.slice(0, 4), players, setRitRun1Equity);
          ritTimerRef.current = setTimeout(runSequence, BOARD_CARD_DISPLAY);
          return;
        }
        // Deal river with dramatic pause
        if (run1CardCount < 5) {
          console.log('🔄 [RIT Run1] Dealing river');
          setRitPhase('run1-river');
          run1CardCount = 5;
          setRitRun1VisibleCards(5);
          calculateRitEquity(run1Cards.slice(0, 5), players, setRitRun1Equity);
          ritTimerRef.current = setTimeout(runSequence, RIVER_SUSPENSE);
          return;
        }
        // Run 1 complete - show winner
        currentPhase = 'run1-winner';
        setRitPhase('run1-winner');
        setRitShowRun1Winner(true);
        console.log('🔄 [RIT Run1] Showing winner');
        ritTimerRef.current = setTimeout(runSequence, WINNER_DISPLAY_TIME);
        return;
      }
      
      // Transition to Run 2 with a pause
      if (currentPhase === 'run1-winner') {
        console.log('🔄 [RIT] Transitioning to Run 2...');
        currentPhase = 'run2-transition';
        setRitPhase('run2-transition');
        ritTimerRef.current = setTimeout(runSequence, RUN_TRANSITION_DELAY);
        return;
      }
      
      if (currentPhase === 'run2-transition') {
        currentPhase = 'run2';
        run2CardCount = startingBoardCount; //  FIX: Start Run 2 from same starting board

        setRitRun2VisibleCards(startingBoardCount);
        // Show equity on starting board for Run 2
        const boardForEquity = run2Cards.slice(0, startingBoardCount);
        calculateRitEquity(boardForEquity, players, setRitRun2Equity);
      }
      
      // ============================================
      // RUN 2 - Same suspense pattern as Run 1
      // ============================================
      if (currentPhase === 'run2') {
        // Deal flop if needed
        if (run2CardCount < 3) {
          console.log('🔄 [RIT Run2] Dealing flop');
          setRitPhase('run2-flop');
          run2CardCount = 3;
          setRitRun2VisibleCards(3);
          calculateRitEquity(run2Cards.slice(0, 3), players, setRitRun2Equity);
          ritTimerRef.current = setTimeout(runSequence, BOARD_CARD_DISPLAY);
          return;
        }
        // Deal turn
        if (run2CardCount < 4) {
          console.log('🔄 [RIT Run2] Dealing turn');
          setRitPhase('run2-turn');
          run2CardCount = 4;
          setRitRun2VisibleCards(4);
          calculateRitEquity(run2Cards.slice(0, 4), players, setRitRun2Equity);
          ritTimerRef.current = setTimeout(runSequence, BOARD_CARD_DISPLAY);
          return;
        }
        // Deal river with dramatic pause
        if (run2CardCount < 5) {
          console.log('🔄 [RIT Run2] Dealing river');
          setRitPhase('run2-river');
          run2CardCount = 5;
          setRitRun2VisibleCards(5);
          calculateRitEquity(run2Cards.slice(0, 5), players, setRitRun2Equity);
          ritTimerRef.current = setTimeout(runSequence, RIVER_SUSPENSE);
          return;
        }
        // Run 2 complete - show winner
        currentPhase = 'run2-winner';
        setRitPhase('run2-winner');
        setRitShowRun2Winner(true);
        console.log('🔄 [RIT Run2] Showing winner');
        ritTimerRef.current = setTimeout(runSequence, WINNER_DISPLAY_TIME);
        return;
      }
      
      // Complete
      if (currentPhase === 'run2-winner') {
        console.log('🔄 [RIT SUSPENSE] Animation complete!');
        setRitPhase('complete');
        ritActiveRef.current = false;
      }
    };
    
    // Start the sequence after a brief delay
    ritTimerRef.current = setTimeout(runSequence, 500);
    
    //  ENTERPRISE: Cleanup does NOT clear timer if animation is active

    // This prevents dependency changes from killing the animation mid-sequence
    // Recovery timeout mechanism handles stuck animations instead
    return () => {
      // Only clear timer if we're starting a new animation (hand number changed)
      // Don't clear if it's just a dependency change
      if (!ritActiveRef.current && ritTimerRef.current) {
        clearTimeout(ritTimerRef.current);
      }
    };
  //  ENTERPRISE: Removed safePlayers from dependencies!

  // safePlayers changes when hole cards are fetched, which was killing the animation.
  // We now use gameState.players directly and store in ref.
  }, [gameState.runItTwiceResults, gameState.handNumber, gameState.players, calculateRitEquity]);

  //  BOMB POT: Show announcement briefly when bomb pot starts

  useEffect(() => {
    if (gameState.isBombPot && gameState.handNumber !== lastBombPotHandRef.current) {
      // New bomb pot hand - show announcement
      lastBombPotHandRef.current = gameState.handNumber;
      setShowBombPotAnnouncement(true);
      
      // Auto-hide after 2 seconds so players can see the flop
      const timer = setTimeout(() => {
        setShowBombPotAnnouncement(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
    
    //  FIX: Clear announcement when round advances past preflop (bomb pot skips to flop)

    if (showBombPotAnnouncement && gameState.round !== 'preflop' && gameState.round !== 'flop') {
      setShowBombPotAnnouncement(false);
    }
  }, [gameState.isBombPot, gameState.handNumber, gameState.round, showBombPotAnnouncement]);

  //  Track the round BEFORE entering showdown (to know how many cards were already dealt)

  const lastPlayingRoundRef = useRef<string>('preflop');
  
  // Update last playing round whenever round changes (before showdown)
  useEffect(() => {
    if (gameState.round !== 'showdown' && gameState.round !== 'ended') {
      lastPlayingRoundRef.current = gameState.round;
      console.log('🎬 [ROUND] Tracking round:', gameState.round);
    }
  }, [gameState.round]);

  // Trigger runout sequence
  useEffect(() => {
    //  DEBUG: Log all conditions at entry

    console.log('🎬 [RUNOUT_CHECK] Entry:', {
      handNumber: gameState.handNumber,
      lastRunoutHand: lastRunoutHandRef.current,
      round: gameState.round,
      allInOccurred: gameState.allInOccurred,
      allInShowdown: gameState.allInShowdown,
      safePlayersCount: safePlayers.length,
      runItTwiceResults: !!gameState.runItTwiceResults,
    });
    
    // Only trigger once per hand when entering showdown with multiple players
    if (gameState.handNumber === lastRunoutHandRef.current) {
      console.log('🎬 [RUNOUT] Skipping - already processed hand', gameState.handNumber);
      return;
    }
    if (gameState.round !== 'showdown' && gameState.round !== 'ended') {
      console.log('🎬 [RUNOUT] Skipping - round is', gameState.round, 'not showdown/ended');
      return;
    }
    
    //  SKIP normal runout if Run It Twice is happening

    if (gameState.runItTwiceResults) {
      console.log('🎬 [RUNOUT] Skipping - Run It Twice results exist');
      return;
    }
    
    //  FIX #1: Use hand-level flag, not player.allIn which gets cleared

    // If allInOccurred or allInShowdown, all non-folded players are part of the showdown
    const hasAllIn = gameState.allInOccurred === true || gameState.allInShowdown === true;
    const allInPlayers = hasAllIn ? safePlayers.filter(p => inShowdownHand(p)) : [];
    
    console.log('🎬 [RUNOUT] hasAllIn:', hasAllIn, 'allInPlayers:', allInPlayers.length, 'safePlayers:', safePlayers.map(p => ({ id: p.id?.substring(0,8), folded: p.folded })));
    
    //  FIX: If not enough players for runout, mark as complete immediately

    // This prevents winner announcement from waiting forever
    if (allInPlayers.length < 2) {
      console.log('🎬 [RUNOUT] Skipping - only', allInPlayers.length, 'players for runout - marking complete');
      lastRunoutHandRef.current = gameState.handNumber;
      //  CRITICAL: Set to 'complete' so winner announcement doesn't wait

      setRunoutPhase('complete');
      runoutActiveRef.current = false;
      return;
    }

    //  CRITICAL: Determine how many community cards were ALREADY visible

    // Based on what round we were on before entering showdown
    const previousRound = lastPlayingRoundRef.current;
    let startingBoardCount = 0;
    switch (previousRound) {
      case 'preflop': startingBoardCount = 0; break;
      case 'flop': startingBoardCount = 3; break;
      case 'turn': startingBoardCount = 4; break;
      case 'river': startingBoardCount = 5; break;
      default: startingBoardCount = 0;
    }
    
    console.log('🎬 [RUNOUT] Starting all-in suspense for', allInPlayers.length, 'players');
    console.log('🎬 [RUNOUT] Previous round:', previousRound, '| Starting board count:', startingBoardCount);
    lastRunoutHandRef.current = gameState.handNumber;
    
    //  CRITICAL: Set ref SYNCHRONOUSLY before any state updates

    runoutActiveRef.current = true;
    //  ENTERPRISE: Track animation start time for recovery timeout

    runoutAnimationStartRef.current = Date.now();
    
    //  FIX: Cache the pot at runout start (use current value or cached value)

    // The pot might already be 0 by now, so use cachedPot if available
    if (gameState.pot > 0) {
      setCachedPot(Math.round(gameState.pot * 100) / 100);
    }
    console.log('🎬 [RUNOUT] Cached pot for display:', cachedPot || gameState.pot);
    
    //  FIXED: Start with existing board cards, don't reset to 0!

    setRunoutPhase('reveal-cards');
    setRevealedPlayerIndex(-1);
    setRevealedCardIndex(-1);
    setVisibleBoardCount(startingBoardCount); // Keep existing cards visible!
    setPlayerEquities({});

    // Clear any existing timer
    if (runoutTimerRef.current) clearTimeout(runoutTimerRef.current);

    // Start the reveal sequence
    let playerIdx = 0;
    let cardIdx = 0;
    let boardCount = startingBoardCount; //  Start from current board, not 0

    
    //  TIMING - Balanced suspense without being too slow

    const CARD_REVEAL_DELAY = 600;      // 0.6s between each card flip
    const PLAYER_SWITCH_DELAY = 800;    // 0.8s pause between players
    const BOARD_CARD_DISPLAY = 2500;    // 2.5s to read each new board card + equity
    const RIVER_SUSPENSE = 2000;        // 2s dramatic pause on river before winner
    
    const sequence = () => {
      // Phase 1: Reveal hole cards one by one
      if (playerIdx < allInPlayers.length) {
        if (cardIdx < 2) {
          setRevealedPlayerIndex(playerIdx);
          setRevealedCardIndex(cardIdx);
          console.log(`🎬 [RUNOUT] Revealing player ${playerIdx} card ${cardIdx}`);
          cardIdx++;
          runoutTimerRef.current = setTimeout(sequence, CARD_REVEAL_DELAY);
        } else {
          // Move to next player
          playerIdx++;
          cardIdx = 0;
          runoutTimerRef.current = setTimeout(sequence, PLAYER_SWITCH_DELAY);
        }
        return;
      }

      // After hole cards revealed, show equity with current board
      // Then continue dealing remaining board cards
      
      // Phase 2: If we started at 0, show flop first
      if (boardCount === 0) {
        console.log('🎬 [RUNOUT] Dealing flop (was preflop all-in)');
        setRunoutPhase('show-equity');
        boardCount = 3;
        setVisibleBoardCount(3);
        calculateEquities(3).catch(err => console.error('Equity calc failed:', err));
        runoutTimerRef.current = setTimeout(sequence, BOARD_CARD_DISPLAY);
        return;
      }
      
      // Phase 2b: If we started at 3 (flop), just calculate equity first
      if (boardCount === 3 && startingBoardCount === 3) {
        console.log('🎬 [RUNOUT] Calculating equity on existing flop');
        setRunoutPhase('show-equity');
        calculateEquities(3).catch(err => console.error('Equity calc failed:', err));
        // Small pause then continue to turn
        runoutTimerRef.current = setTimeout(() => {
          boardCount = 3.5; // Flag to move to turn next
          sequence();
        }, BOARD_CARD_DISPLAY);
        return;
      }

      // Phase 3: Deal turn (if needed)
      if (boardCount === 3 || boardCount === 3.5) {
        console.log('🎬 [RUNOUT] Dealing turn');
        setRunoutPhase('deal-turn');
        boardCount = 4;
        setVisibleBoardCount(4);
        runoutTimerRef.current = setTimeout(() => {
          calculateEquities(4).catch(err => console.error('Equity calc failed:', err));
          runoutTimerRef.current = setTimeout(sequence, BOARD_CARD_DISPLAY);
        }, 500);
        return;
      }
      
      // Phase 3b: If we started at 4 (turn), just calculate equity first
      if (boardCount === 4 && startingBoardCount === 4) {
        console.log('🎬 [RUNOUT] Calculating equity on existing turn');
        setRunoutPhase('show-equity');
        calculateEquities(4).catch(err => console.error('Equity calc failed:', err));
        // Small pause then continue to river
        runoutTimerRef.current = setTimeout(() => {
          boardCount = 4.5; // Flag to move to river next
          sequence();
        }, BOARD_CARD_DISPLAY);
        return;
      }

      // Phase 4: Deal river
      if (boardCount === 4 || boardCount === 4.5) {
        console.log('🎬 [RUNOUT] Dealing river');
        setRunoutPhase('deal-river');
        boardCount = 5;
        setVisibleBoardCount(5);
        runoutTimerRef.current = setTimeout(() => {
          //  CRITICAL: Clear ref when animation completes

          runoutActiveRef.current = false;
          setRunoutPhase('complete');
          console.log('🎬 [RUNOUT] Sequence complete - announcing winner now');
        }, RIVER_SUSPENSE);
        return;
      }
      
      // Phase 5: If started at river (5), just complete immediately after showing cards
      if (boardCount === 5 || startingBoardCount === 5) {
        console.log('🎬 [RUNOUT] River already dealt, completing');
        runoutActiveRef.current = false;
        setRunoutPhase('complete');
        return;
      }
    };

    // Start after short delay
    runoutTimerRef.current = setTimeout(sequence, 500);

    return () => {
      if (runoutTimerRef.current) clearTimeout(runoutTimerRef.current);
    };
  //  CRITICAL FIX: Removed allInShowdownPlayers.length from dependencies!

  // When server sends showdown result, player state changes and allInShowdownPlayers
  // would recompute to empty array, canceling the animation.
  // handNumber + round is sufficient to trigger once per showdown.
  }, [gameState.handNumber, gameState.round]);

  // ========================================================================
  //  ENTERPRISE: Resolve showdown after animation completes

  // ========================================================================
  // When runout animation finishes, call the server to determine winner
  // This enables suspense UI while keeping winner determination server-side
  // ========================================================================
  const lastResolvedHandRef = useRef<number>(0);
  
  useEffect(() => {
    // Only trigger when:
    // 1. Runout animation just completed
    // 2. We're actually in showdown/ended round (not still betting)
    // 3. We're in an all-in showdown that needs resolution
    // 4. We haven't already resolved this hand
    // 5. There's no winner yet
    if (runoutPhase !== 'complete') return;
    if (gameState.round !== 'showdown' && gameState.round !== 'ended') return; //  FIX: Prevent premature call

    if (!gameState.allInShowdown && !gameState.allInOccurred) return;
    if (gameState.showdownComplete) return;
    if (gameState.winners && gameState.winners.length > 0) return;
    if (lastResolvedHandRef.current === gameState.handNumber) return;
    
    console.log('🎰 [RESOLVE] Animation complete - calling server to resolve showdown');
    lastResolvedHandRef.current = gameState.handNumber;
    
    // Call server to resolve showdown
    gameApiService.resolveAllInShowdown(tableId)
      .then(result => {
        if (result.success) {
          console.log('🎰 [RESOLVE] Showdown resolved:', result.winners?.length || 0, 'winner(s)');
        } else {
          console.error('🎰 [RESOLVE] Failed:', result.error);
        }
      })
      .catch(err => {
        console.error('🎰 [RESOLVE] Error:', err);
      });
  }, [runoutPhase, gameState.allInShowdown, gameState.allInOccurred, gameState.showdownComplete, gameState.winners, gameState.handNumber, tableId]);

  // Helper: Check if a specific card should be visible during runout
  const isCardRevealed = useCallback((playerIndex: number, cardIndex: number) => {
    // Cards flip one at a time only during a reveal phase (plain runout or
    // Run It Multiple Times); every other phase shows them all.
    const revealing = runoutPhase === 'reveal-cards' || ritPhase === 'reveal-cards';
    if (!revealing) return true;
    if (playerIndex < revealedPlayerIndex) return true;
    if (playerIndex === revealedPlayerIndex && cardIndex <= revealedCardIndex) return true;
    return false;
  }, [runoutPhase, ritPhase, revealedPlayerIndex, revealedCardIndex]);

  // Run It Multiple Times on the felt: one board row per run for the SVG,
  // and the live percentages of whichever run is dealing for the seat badges.
  const ritActive = ritPhase !== 'none';
  const ritBoardsForSVG = useMemo(() => {
    const rr: any = (gameState as any).runItTwiceResults;
    if (!rr || !ritActive) return undefined;
    const runs: any[] = Array.isArray(rr.runs) && rr.runs.length >= 2 ? rr.runs : (rr.run1 && rr.run2 ? [rr.run1, rr.run2] : []);
    if (runs.length < 2) return undefined;
    const winnerLine = (run: any) => {
      const rows: any[] = Array.isArray(run?.winners) && run.winners.length > 0 ? run.winners : (run?.winner?.playerId ? [run.winner] : []);
      if (rows.length === 0) return '';
      const who = rows.map((w: any) => usernames[w.playerId] || 'Player').join(' & ');
      const total = rows.reduce((s: number, w: any) => s + Number(w.amount || 0), 0);
      const hand = rows[0]?.hand;
      return rows.length > 1
        ? `Chop · ${who} split $${total.toFixed(2)}${hand ? ` · ${hand}` : ''}`
        : `${who} wins $${total.toFixed(2)}${hand ? ` · ${hand}` : ''}`;
    };
    // Rows follow the timeline: finished runs are fully shown with their
    // winner, the run currently dealing shows ritPartialCards, later runs
    // are not on the felt yet.
    const done = ritPhase === 'complete';
    const rows: Array<{ cards: any[]; visible: number; label: string; winnerText?: string; active: boolean }> = [];
    runs.forEach((run: any, r: number) => {
      const finished = done || r < ritRunsVisible;
      const dealing = !done && r === ritRunsVisible;
      if (!finished && !dealing) return;
      rows.push({
        cards: run?.communityCards || [],
        visible: finished ? 5 : ritPartialCards,
        label: `RUN ${r + 1}`,
        winnerText: finished ? winnerLine(run) : '',
        active: dealing || (done && r === runs.length - 1),
      });
    });
    return rows;
  }, [(gameState as any).runItTwiceResults, ritActive, ritPhase, ritRunsVisible, ritPartialCards, usernames]);
  const equitiesForSVG = ritActive && ritPhase !== 'complete' ? ritLiveEquity : playerEquities;

  // Override visible community cards during runout
  const runoutVisibleCards = useMemo(() => {
    if (runoutPhase === 'none' || runoutPhase === 'complete') {
      return null; // Use default behavior
    }
    return gameState.communityCards.slice(0, visibleBoardCount);
  }, [runoutPhase, gameState.communityCards, visibleBoardCount]);

  //  GAME ITEMS: Load PSS Coins balance

  useEffect(() => {
    const loadBalance = async () => {
      if (!currentUserId) return;
      const wallet = await walletService.getWallet(currentUserId);
      if (wallet) {
        setPkCashBalance(wallet.pkCash);
      }
    };
    loadBalance();
  }, [currentUserId]);

  //  GAME ITEMS: Subscribe to animations at this table

  useEffect(() => {
    if (!tableId) return;
    
    console.log('🎬 [PokerTable] Setting up animation subscription for table:', tableId);
    
    const unsubscribe = gameItemsService.subscribeToAnimations(
      tableId,
      (animations) => {
        console.log('🎬 [PokerTable] Received animations:', animations.length, animations);
        setActiveAnimations(animations);
      }
    );
    
    return () => {
      console.log('🎬 [PokerTable] Cleaning up animation subscription');
      unsubscribe();
    };
  }, [tableId]);

  //  Handle show cards click - uses secure API to reveal cards

  const handleShowCards = useCallback(async () => {
    setShowCardsChosen(true);
    try {
      //  SECURITY: Call server endpoint that reads real cards from secure storage

      // and updates gameState with revealed cards for all players to see
      const result = await gameApiService.showCards(tableId);
      if (!result.success) {
        console.error('Failed to show cards:', result.error);
      } else {
        console.log('✅ Cards revealed:', result.cards);
      }
    } catch (err) {
      console.error('Failed to show cards:', err);
    }
  }, [tableId]);

  //  GAME ITEMS: Handle balance change after item purchase

  const handleBalanceChange = useCallback((newBalance: number) => {
    setPkCashBalance(newBalance);
  }, []);

  //  GAME ITEMS: Handle player positions update from SVG

  //  FIXED: Compare positions to prevent infinite loops

  const handlePlayerPositionsUpdate = useCallback((positions: { [id: string]: { x: number; y: number } }) => {
    setPlayerPositions(prev => {
      // Only update if positions actually changed
      const prevKeys = Object.keys(prev).sort().join(',');
      const newKeys = Object.keys(positions).sort().join(',');
      if (prevKeys !== newKeys) return positions;
      
      // Check if any position changed
      for (const id of Object.keys(positions)) {
        if (!prev[id] || prev[id].x !== positions[id].x || prev[id].y !== positions[id].y) {
          return positions;
        }
      }
      return prev; // No change, keep same reference
    });
  }, []);

  //  FIXED: Get player display name - prioritize usernames prop over everything else

  const getPlayerDisplayName = (userId: string): string => {
    // First priority: usernames prop from GamePage (most accurate)
    if (usernames && typeof usernames === 'object' && usernames[userId]) {
      return usernames[userId];
    }
    // Second priority: player.name in gameState (if set correctly)
    const player = safePlayers.find(p => p.id === userId);
    if (player?.name && player.name !== userId && !player.name.startsWith('Player ') && player.name.length < 20) {
      return player.name;
    }
    // Fallback: truncated user ID
    return `Player ${userId.substring(0, 6)}`;
  };

  //  INVITE FRIENDS HANDLER - Opens invite modal

  const handleInvite = useCallback(() => {
    setShowInviteModal(true);
  }, []);

  //  Handle clicking empty seat to join table

  const handleEmptySeatClick = useCallback((seatIndex: number) => {
    // Check if user is already seated
    const userAlreadySeated = safePlayers.some(p => p.id === currentUserId);
    if (userAlreadySeated) {
      console.log('User already seated');
      return;
    }
    // Trigger join table flow via onSeatClick callback
    if (onSeatClick) {
      onSeatClick(seatIndex);
    }
  }, [safePlayers, currentUserId, onSeatClick]);

  //  Transform players for PokerTableSVG (add name and photoURL)

  //  FIXED: Safe dependency - removed JSON.stringify(safePlayers.map()) which crashes on null

  const playersForSVG = useMemo(() => {
    // Filter again to be absolutely safe
    const validPlayers = safePlayers.filter(p => p && p.id);
    return validPlayers.map(player => ({
      ...player,
      name: getPlayerDisplayName(player.id),
      // House bots get PSS character art (same faces as the premium felt)
      photoURL: String(player.id).startsWith('house_bot_')
        ? pssBustForBot(player.id)
        : (userPhotos[player.id] || player.photoURL),
    }));
  }, [safePlayers, usernames, userPhotos]);

  //  Transform handResult for PokerTableSVG

  const handResultForSVG = useMemo(() => {
    if (!gameState.winners?.length) return null;
    
    const winner = gameState.winners?.[0];
    if (!winner) return null;
    
    // Check if this was a fold win - either from handResult or by checking if all others folded
    const nonFoldedPlayers = safePlayers.filter(p => !p.folded && !p.eliminated);
    const detectedFoldWin = nonFoldedPlayers.length === 1 && nonFoldedPlayers[0].id === winner.playerId;
    const isFoldWin = gameState.handResult?.allOthersFolded === true || detectedFoldWin;
    
    return {
      winnerId: winner.playerId,
      winnerName: getPlayerDisplayName(winner.playerId),
      winningHand: winner.hand ? { type: winner.hand } : undefined,
      potWon: winner.amount,
      allOthersFolded: isFoldWin,
      winnerShowedCards: gameState.handResult?.winnerShowedCards || false,
    };
  }, [gameState.handResult, gameState.winners, gameState.players, usernames]);

  // Get player at specific seat
  const getPlayerAtSeat = (seatIndex: number) => {
    return safePlayers.find(p => p.seat === seatIndex);
  };

  // Seat positions for 8-player oval table (ReBet style)
  const SEAT_POSITIONS = [
    { seatIndex: 0, position: 'bottom' },
    { seatIndex: 1, position: 'top' },
    { seatIndex: 2, position: 'left' },
    { seatIndex: 3, position: 'right' },
    { seatIndex: 4, position: 'top-left' },
    { seatIndex: 5, position: 'top-right' },
    { seatIndex: 6, position: 'bottom-left' },
    { seatIndex: 7, position: 'bottom-right' },
  ];

  // Detect bust notifications
  useEffect(() => {
    if (gameState.bustNotifications && gameState.bustNotifications.length > 0) {
      const bust = gameState.bustNotifications[0];
      const bustId = `${bust.playerId}-${bust.handNumber}`;
      
      if (bustId !== lastBustNotificationId) {
        setLastBustNotificationId(bustId);
        
        if (bust.playerId === currentUserId) {
          setNotificationData({
            type: 'bust',
            title: 'You Busted!',

            message: 'You ran out of chips!',
          });
        } else {
          setNotificationData({
            type: 'info',
            title: `${bust.playerName} Busted`,
            message: `${bust.playerName} ran out of chips`,
          });
        }
        setShowNotification(true);
      }
    }
  }, [gameState.bustNotifications, currentUserId, lastBustNotificationId]);

  // Detect winner - Show notification only (NO MODAL)
  //  RUNOUT: Wait for animation to complete before announcing

  useEffect(() => {
    // Don't process if round isn't ended yet
    if (gameState.round !== 'ended' || !gameState.winners || gameState.winners.length === 0) {
      return;
    }
    
    //  FIX #4: Check if this is a fold-win or all-in showdown

    // Use gameState.allInShowdown (persists) instead of player.allIn (gets cleared!)
    const isFoldWin = gameState.handResult?.allOthersFolded === true;
    const hasRitResults = !!gameState.runItTwiceResults;
    
    //  FOLD-WIN FAST PATH: Announce immediately, no runout needed

    if (isFoldWin) {
      console.log('🏆 [WINNER] Fold win detected - announcing immediately');
    } else {
      //  FIX #1: Use allInOccurred OR allInShowdown - NEVER player.allIn

      const needsRunout = gameState.allInOccurred === true || gameState.allInShowdown === true;
      // Check both regular runout AND RIT completion
      const runoutComplete = runoutPhase === 'complete' || (hasRitResults && ritPhase === 'complete');
      if (needsRunout && !runoutComplete) {
        console.log('⏳ [WINNER] Waiting for runout to complete... phase:', runoutPhase, 'ritPhase:', ritPhase);
        return;
      }
    }
    
    // ========================================================================
    //  ENTERPRISE SPLIT POT DETECTION

    // - True split pot = same hand, multiple winners (e.g., both have Full House)
    // - Side pots = different hands, multiple winners (each won different pot)
    // - RIT = has .run property (handled separately by RIT animation)
    // ========================================================================
    // Uncalled-bet returns ride in winners[] flagged `uncalled` - they are
    // a player's own chips coming back, never a win. Run-it-twice rows come
    // one per run per winner; merge them per player so the notice judges
    // the whole hand (a chopped run and a lost run can still be a loss).
    const winners = aggregateWinnerRows((gameState.winners || []).filter((w: any) => !w.uncalled));
    const hasMultipleWinners = winners.length > 1;
    const isRunItTwice = winners.some((w: any) => w.run !== undefined);
    
    // True split = all winners have identical hand AND not RIT
    const isTrueSplitPot = hasMultipleWinners && 
      !isRunItTwice && 
      winners.every((w: any) => w.hand === winners[0].hand);
    
    // Check if current user won anything
    const currentUserWin = winners.find((w: any) => w.playerId === currentUserId);
    const totalPot = winners.reduce((sum: number, w: any) => sum + (w.amount || 0), 0);
    
    // Use combined winner IDs for dedup
    const winnerId = `${winners.map((w: any) => w.playerId).join('-')}-${gameState.handNumber}`;
    
    if (winnerId !== lastWinnerNotificationId) {
      console.log('🏆 [WINNER] Announcing winner(s):', winners.length, 'isSplitPot:', isTrueSplitPot, 'isRIT:', isRunItTwice);
      setLastWinnerNotificationId(winnerId);
      
      if (currentUserWin) {
        // A winners[] row is not a win. A covered player who lost the
        // showdown gets their uncalled chips or a side pot back as a row
        // too, and that used to fire the celebration, the win jingle and
        // "Share this win" on a hand they lost. Judge by NET first.
        const myRowEarly = safePlayers.find((p: any) => p && p.id === currentUserId);
        const myInvestedEarly = Math.round(((myRowEarly as any)?.totalBetThisHand || 0) * 100) / 100;
        const myNetEarly = Math.round((currentUserWin.amount - myInvestedEarly) * 100) / 100;
        const isFoldWinEarly = gameState.handResult?.allOthersFolded === true || (currentUserWin as any).hand === 'Fold Win';
        const reallyWon = isFoldWinEarly || myNetEarly > 0;

        //  TRIGGER CELEBRATION FOR WINNER

        if (reallyWon && winnerId !== lastCelebrationHandId) {
          setLastCelebrationHandId(winnerId);
          setCelebrationAmount(currentUserWin.amount);
          setCelebrationHandDesc(currentUserWin.handDescription);
          setCelebrationLoserDesc(currentUserWin.loserHandDescription);
          setCelebrationIsFoldWin(isFoldWinEarly);
          setShowCelebration(true);

          //  PLAY WIN SOUND

          playActionSound('win', soundEnabled);
          
          //  STORIES: Show "Share Win" button if win is shareable

          const currency = gameState.currency || 'playcoins';
          const isShareable = isShareableWin(
            currentUserWin.amount,
            currency as 'pkcash' | 'playcoins',
            gameState.bigBlind
          );
          
          if (isShareable) {
            // Get hand type from winner data
            const handType = currentUserWin.hand || currentUserWin.handName || undefined;
            
            // Collect opponent info for the win card
            const nonFoldedPlayers = safePlayers.filter(p => !p.folded && !p.eliminated);
            const wasShowdown = nonFoldedPlayers.length > 1 || gameState.allInShowdown;
            
            // Get all players who lost (everyone except current user)
            const opponents = safePlayers
              .filter(p => p.id !== currentUserId && !p.eliminated)
              .map(p => {
                // Find if they had a hand result
                const playerWinData = winners.find((w: any) => w.playerId === p.id);
                return {
                  odD: p.id,
                  username: getPlayerDisplayName(p.id),
                  hand: playerWinData?.hand || undefined,
                  folded: p.folded || false,
                };
              });
            
            setShareWinData({
              potAmount: currentUserWin.amount,
              handType,
              opponents,
              wasShowdown,
            });
            setShowShareWinButton(true);
            
            // Auto-hide after 8 seconds
            setTimeout(() => {
              setShowShareWinButton(false);
            }, 8000);
          }
        }
        
        //  TRUE SPLIT POT: Show split message

        // Judge by NET, not by having a winners[] row. A returned side pot
        // or an uncalled bet puts a row in winners[] too - and announcing
        // "You Won" (or worse, "Split Pot") to a player who just LOST the
        // showdown while covered reads as either a lie or a payout bug.
        const myRow = safePlayers.find((p: any) => p && p.id === currentUserId);
        const myInvested = Math.round(((myRow as any)?.totalBetThisHand || 0) * 100) / 100;
        const myNet = Math.round((currentUserWin.amount - myInvested) * 100) / 100;
        const otherWinners = winners.filter((w: any) => w.playerId !== currentUserId);
        const topOther = [...otherWinners].sort((a: any, b: any) => (b.amount || 0) - (a.amount || 0))[0];

        if (myNet < 0 && topOther) {
          // Covered loss with money back: a loss, said plainly, with the
          // returned chips explained instead of dressed up as a win.
          playActionSound('lose', soundEnabled);
          setNotificationData({
            type: 'lose',
            title: `${getPlayerDisplayName(topOther.playerId)} won the hand`,
            handName: topOther.hand || null,
            message: `You lost $${Math.abs(myNet).toFixed(2)}`,
            detailLines: [`$${currentUserWin.amount.toFixed(2)} returned to you (side pot / uncalled chips)`],
          });
        } else if (isTrueSplitPot) {
          const otherNames = otherWinners.map((w: any) => getPlayerDisplayName(w.playerId)).join(' & ');
          setNotificationData({
            type: 'win',
            title: 'Split Pot - You Won',
            handName: currentUserWin.hand || null,
            message: `$${currentUserWin.amount.toFixed(2)} to you`,
            detailLines: [`Chopped with ${otherNames}`],
          });
        } else if (topOther && myNet >= 0) {
          // 3-way cover case: someone else took the main pot but the side
          // pot leaves this player ahead - a real win, sized honestly.
          setNotificationData({
            type: 'win',
            title: 'You Won the Side Pot',
            handName: currentUserWin.hand || null,
            message: `$${currentUserWin.amount.toFixed(2)} to you (up $${myNet.toFixed(2)})`,
            detailLines: [`Main pot went to ${getPlayerDisplayName(topOther.playerId)}`],
          });
        } else {
          setNotificationData({
            type: 'win',
            title: 'You Won',
            handName: currentUserWin.hand || null,
            message: `$${currentUserWin.amount.toFixed(2)} added to your stack`,
          });
        }
      } else {
        //  PLAY LOSE SOUND (only if we were in the hand)

        const wasInHand = safePlayers.some(p => p.id === currentUserId && !p.folded);
        if (wasInHand) {
          playActionSound('lose', soundEnabled);
        }
        
        //  TRUE SPLIT POT: Show all winners

        if (isTrueSplitPot) {
          const winnerNames = winners.map((w: any) => getPlayerDisplayName(w.playerId)).join(' & ');
          const splitHand = winners.find((w: any) => w.hand)?.hand || null;
          setNotificationData({
            type: 'lose',
            title: 'Split Pot',
            handName: splitHand,
            message: `${winnerNames} chopped the $${totalPot.toFixed(2)} pot`,
          });
        } else {
          // Single winner OR side pot - show primary winner (highest amount)
          const primaryWinner = [...winners].sort((a: any, b: any) => (b.amount || 0) - (a.amount || 0))[0];
          const myUncalledRow = (gameState.winners || []).find((w: any) => w.playerId === currentUserId && w.uncalled);
          setNotificationData({
            type: 'lose',
            title: `${getPlayerDisplayName(primaryWinner.playerId)} won the hand`,
            handName: primaryWinner.hand || null,
            message: `$${primaryWinner.amount.toFixed(2)} pot`,
            ...(myUncalledRow ? { detailLines: [`$${Number(myUncalledRow.amount || 0).toFixed(2)} of your uncalled chips returned to you`] } : {}),
          });
        }
      }
      setShowNotification(true);
    }
  }, [gameState.round, gameState.winners, gameState.handNumber, gameState.players, gameState.handResult, currentUserId, lastWinnerNotificationId, lastCelebrationHandId, usernames, runoutPhase, ritPhase, gameState.runItTwiceResults, soundEnabled, safePlayers, gameState.allInOccurred, gameState.allInShowdown]);

  //  UPDATED: Auto-start new hand - check for enough active players

  //  RUNOUT: Wait for animation to complete before starting timer

  useEffect(() => {
    // Don't process if round isn't ended yet
    if (gameState.round !== 'ended' || !gameState.winners || gameState.winners.length === 0) {
      return;
    }

    // 🏆 Tournament tables: the server's tournament scheduler owns ALL
    // dealing (breaks, blind levels, eliminations, consolidation). Client
    // auto-start must never call /startHand or resetTableToLobby here.
    if ((gameState as any).tournamentId || (gameState as any).isTournament
      || (gameState as any).tournamentTableNumber != null) {
      return;
    }

    //  FIX #1: Check if this is a fold-win or all-in showdown

    // Use allInOccurred OR allInShowdown - NEVER player.allIn!
    const isFoldWin = gameState.handResult?.allOthersFolded === true;
    const isAllInShowdown = gameState.allInOccurred === true || gameState.allInShowdown === true;
    
    //  FOLD-WIN FAST PATH: gameProgressionService handles these

    if (isFoldWin) {
      console.log('🎯 [AUTO-START] Fold win - gameProgressionService will handle');
      return; // Let gameProgressionService start next hand after 8s
    }
    
    //  ALL-IN SHOWDOWN: PokerTable handles these (after runout animation)

    if (isAllInShowdown) {
      // Check both regular runout AND RIT completion
      const hasRitResults = !!gameState.runItTwiceResults;
      const runoutComplete = runoutPhase === 'complete' || (hasRitResults && ritPhase === 'complete');
      if (!runoutComplete) {
        console.log('⏳ [AUTO-START] Waiting for runout... phase:', runoutPhase, 'ritPhase:', ritPhase);
        return;
      }
      console.log('✅ [AUTO-START] Runout complete - PokerTable will start next hand');
    } else {
      // Edge case - not a fold win, not an all-in... let gameProgressionService handle
      console.log('🎯 [AUTO-START] Standard win - gameProgressionService will handle');
      return;
    }
    
    // ⏰ Start countdown display - shows time remaining after runout animation
    //  FIX: Increased to 8s to give players time to rebuy

    const COUNTDOWN_AFTER_ANIMATION = 8;
    console.log(`⏰ [AUTO-START] Starting ${COUNTDOWN_AFTER_ANIMATION}-second timer for next hand`);
    
    setNextHandCountdown(COUNTDOWN_AFTER_ANIMATION);
    const countdownInterval = setInterval(() => {
      setNextHandCountdown(prev => Math.max(0, prev - 1));
    }, 1000);
    
    const timer = setTimeout(async () => {
      clearInterval(countdownInterval);
        try {
          //  CRITICAL: Use ref to get LATEST gameState (avoids stale closure from reload during delay)

          const currentGameState = gameStateRef.current;
          
          //  SAFETY: Filter out null players first

          const validPlayers = (currentGameState.players || []).filter((p: any) => p && p.id);
          
          //  Get ALL players (including waiting ones)

          const allPlayers = validPlayers.filter(p => !p.eliminated);
          
          //  LOG: Show current chip counts before starting new hand

          console.log('🎰 [AUTO-START] Current player chips:', allPlayers.map(p => `${p.name || p.id.substring(0, 8)}: ${p.chips}`));
          
          //  FIX: Count players who have chips OR are in the process of rebuying

          // If someone has needsRebuy=true, they might be about to rebuy - don't kick them
          const playersWithChipsOrRebuying = allPlayers.filter(
            p => (p.chips > 0 || p.needsRebuy) && !p.waitingForNextHand
          );
          
          //  Count players who definitely have chips

          const playersWithChips = allPlayers.filter(p => p.chips > 0 && !p.waitingForNextHand);
          
          console.log('🎰 [AUTO-START] Players with chips:', playersWithChips.map(p => `${p.name || p.id.substring(0, 8)}: ${p.chips}`));
          
          //  FIX: Need at least 2 players with chips to continue

          // If not enough, wait 2 more seconds for late rebuys before resetting
          if (playersWithChips.length < 2) {
            console.log('⏸️ [AUTO-START] Not enough players with chips - waiting 2s for late rebuys...');
            
            //  Secondary delay to catch rebuys that are in-flight

            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Re-fetch the LATEST gameState after the delay
            const latestGameState = gameStateRef.current;
            const latestPlayers = (latestGameState.players || []).filter((p: any) => p && p.id && !p.eliminated);
            const latestWithChips = latestPlayers.filter(p => p.chips > 0 && !p.waitingForNextHand);
            
            console.log('🎰 [AUTO-START] After 2s delay, players with chips:', latestWithChips.map(p => `${p.name || p.id.substring(0, 8)}: ${p.chips}`));
            
            //  Final check - still not enough players? Reset to lobby

            if (latestWithChips.length < 2) {
              console.log('⏸️ [AUTO-START] Still not enough players after delay - resetting to lobby');
              await tableService.resetTableToLobby(tableId);
              return;
            }
            // Otherwise, continue to start the next hand
          }
          
          //  START HAND: For all-in showdowns, PokerTable starts the hand

          // gameProgressionService skips all-in showdowns, so we handle it here
          console.log('🎰 [AUTO-START] Starting next hand after showdown...');
          await gameApiService.startHand(tableId);
          setLastBustNotificationId(null);
        } catch (err) {
          console.error('Failed to start new hand:', err);
        }
      }, 8000); // 8 seconds after animation completes (increased from 5s)
      
      //  CRITICAL: Return cleanup to prevent double-starting

      // gameProgressionService may also try to start - this prevents race
      
      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
  }, [gameState.round, gameState.winners, gameState.players, gameState.handResult, gameState.smallBlind, gameState.bigBlind, tableId, usernames, runoutPhase, ritPhase, gameState.runItTwiceResults, gameState.allInOccurred, gameState.allInShowdown]);

  // ⏰ Tournament countdown: the SERVER deals the next hand ~5s after the
  // end (client auto-start is disabled on tournament tables), so drive the
  // banner countdown from the hand end so it reads true instead of sitting
  // frozen at its initial value.
  useEffect(() => {
    const isTournamentTable = (gameState as any).tournamentId || (gameState as any).isTournament
      || (gameState as any).tournamentTableNumber != null;
    if (!isTournamentTable || gameState.round !== 'ended') return;
    setNextHandCountdown(5);
    const iv = setInterval(() => setNextHandCountdown(prev => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(iv);
  }, [gameState.round, gameState.handNumber]);

  // Calculate betting values
  const maxBet = useMemo(() => Math.max(0, ...safePlayers.map(p => p.currentBet || 0)), [safePlayers]);
  const callAmount = useMemo(() => currentPlayer ? maxBet - currentPlayer.currentBet : 0, [maxBet, currentPlayer]);
  //  FIXED: Min raise = big blind OR last raise amount, whichever is larger (proper Texas Hold'em rules)

  const minRaise = useMemo(() => {
    const lastRaise = gameState.lastRaiseAmount || gameState.bigBlind;
    return Math.max(gameState.bigBlind, lastRaise);
  }, [gameState.bigBlind, gameState.lastRaiseAmount]);

  // Initialize the raise slider ONCE per decision point (turn/street), keyed
  // on scalars. Keying on the currentPlayer OBJECT meant every Firestore
  // snapshot re-ran this and stomped whatever the player had dialed in -
  // pressing the ALL-IN preset "reverted to min" a second later.
  const myTurnKey = (isCurrentPlayerTurn && currentPlayer)
    ? `${gameState.handNumber}:${gameState.round}:${maxBet}`
    : '';
  useEffect(() => {
    if (!myTurnKey || !currentPlayer) return;
    setRaiseAmount(Math.min(minRaise, currentPlayer.chips));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myTurnKey]);

  // Get possible actions
  const getPossibleActions = () => {
    if (!currentPlayer || !isCurrentPlayerTurn || currentPlayer.folded || currentPlayer.eliminated) return [];

    const actions: string[] = [];
    
    //  ALL-IN-OR-FOLD MODE: Only allow fold and all-in

    const isAoFMode = specialRules?.allInOrFold === true;
    
    if (isAoFMode) {
      // AoF mode - only fold and all-in
      if (!currentPlayer.allIn) actions.push('fold');
      if (currentPlayer.chips > 0) actions.push('all-in');
      return actions;
    }
    
    // Standard mode - all actions available
    if (!currentPlayer.allIn) actions.push('fold');
    
    if (callAmount === 0) {
      actions.push('check');
    } else if (currentPlayer.chips >= callAmount) {
      actions.push('call');
    }
    
    if (currentPlayer.chips > callAmount) {
      actions.push('raise');
    }
    
    if (currentPlayer.chips > 0) {
      actions.push('all-in');
    }

    return actions;
  };

  const possibleActions = getPossibleActions();
  
  //  AoF Mode indicator

  const isAoFMode = specialRules?.allInOrFold === true;

  // Handle player action

  //  RUN IT TWICE: Handle player response

  const handleRITResponse = async (accept: boolean) => {
    if (!tableId || ritResponding) return;
    
    setRitResponding(true);
    try {
      const result = await gameApiService.respondToRunItTwice(tableId, accept);
      console.log(`🔄 [RIT] Response sent: ${accept ? 'ACCEPT' : 'DECLINE'}`, result);
      
      if (result.success) {
        setShowRITModal(false);
      } else {
        console.error('🔄 [RIT] Failed:', result.error);
      }
    } catch (err) {
      console.error('🔄 [RIT] Error:', err);
    } finally {
      setRitResponding(false);
    }
  };

  // RIT decision clock: 25 seconds, then "run it once" is answered for you,
  // so a missed tap can never hold the other player hostage (the server's
  // sweep is the backstop at 60s; a connected client answers long before).
  const [ritCountdown, setRitCountdown] = useState<number | null>(null);
  useEffect(() => {
    const offer: any = (gameState as any).runItTwiceOffer;
    const pending = showRITModal && offer && !offer.allResponded && !offer.declined
      && offer.players?.includes(currentUserId) && offer.responses?.[currentUserId] === undefined;
    if (!pending) { setRitCountdown(null); return; }
    const startedAt = typeof offer.offeredAt === 'number' ? offer.offeredAt : Date.now();
    const tick = () => {
      const left = Math.max(0, 25 - Math.floor((Date.now() - startedAt) / 1000));
      setRitCountdown(left);
      if (left <= 0 && !ritResponding) handleRITResponse(false);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRITModal, (gameState as any).runItTwiceOffer, currentUserId]);

  //  STRADDLE: Post straddle

  const handlePostStraddle = async () => {
    if (!tableId || postingStraddle || !canStraddle) return;
    
    setPostingStraddle(true);
    try {
      const result = await gameApiService.postStraddle(tableId);
      console.log(`📈 [STRADDLE] Posted:`, result);
      
      if (result.success) {
        setStraddlePosted(true);
        setCanStraddle(false);
      } else {
        console.error('📈 [STRADDLE] Failed:', result.error);
        alert(`Failed to post straddle: ${result.error}`);
      }
    } catch (err) {
      console.error('📈 [STRADDLE] Error:', err);
    } finally {
      setPostingStraddle(false);
    }
  };

  // ============================================================================
  //  REBUY HANDLER - Called when player confirms rebuy amount

  // ============================================================================
  const handleRebuy = async (amount: number) => {
    if (!tableId || !currentUserId) return;
    
    setIsRebuyProcessing(true);
    try {
      console.log(`💰 [REBUY] Requesting rebuy: $${amount}`);
      
      //  FIX: Use gameApiService which has correct API URL

      const result = await gameApiService.secureRebuy(tableId, amount);
      
      if (result.success) {
        console.log(`✅ [REBUY] Success! New chip count: ${result.newChipCount}`);
        setShowRebuyModal(false);
        // The Firestore listener will update gameState automatically
      } else {
        console.error('❌ [REBUY] Failed:', result.error);
        alert(`Rebuy failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('❌ [REBUY] Error:', err);
      alert('Rebuy failed. Please try again.');
    } finally {
      setIsRebuyProcessing(false);
    }
  };

  // ============================================================================
  //  ENTERPRISE: Secure Player Action Handler

  // ============================================================================
  // All game logic now runs on the server. Client just sends action request
  // and receives updated state via onSnapshot listener.
  // ============================================================================
  const handlePlayerAction = async (action: string) => {
    //  ENTERPRISE: Use comprehensive guard to prevent invalid actions

    if (!canTakeAction) {
      console.warn(`⚠️ [PokerTable] Action blocked - canTakeAction=false, round=${gameState.round}, status=${gameState.status}`);
      return;
    }

    //  Additional validation for edge cases

    if (!tableId || !currentPlayer) {
      console.warn(`⚠️ [PokerTable] Action blocked - missing tableId or currentPlayer`);
      return;
    }

    setIsProcessing(true);
    setActionError(null);
    setShowRaiseSlider(false);

    try {
      // Play sound immediately for responsiveness
      playActionSound(action, soundEnabled);
      
      // Voice announcement for current user's action
      const playerName = getPlayerDisplayName(currentUserId);
      switch (action) {
        case 'fold':
          voiceAnnouncer.announceFold(playerName);
          break;
        case 'check':
          voiceAnnouncer.announceCheck(playerName);
          break;
        case 'call':
          voiceAnnouncer.announceCall(playerName);
          break;
        case 'raise':
          voiceAnnouncer.announceRaise(playerName, raiseAmount);
          break;
        case 'all-in':
          voiceAnnouncer.announceAllIn(playerName);
          break;
      }

      //  ENTERPRISE: Send action to secure server API with version tracking

      // Server handles ALL validation and state updates
      const result = await gameApiService.playerAction(
        tableId,
        action as 'fold' | 'check' | 'call' | 'raise' | 'all-in',
        action === 'raise' ? raiseAmount : undefined,
        gameState.handId,
        gameState.stateVersion
      );

      if (!result.success) {
        //  ENTERPRISE: Don't show error for stale state - just log

        if (result.error?.includes('state has changed') || result.error?.includes('hand has changed')) {
          console.log(`⚠️ [PokerTable] Stale action ignored: ${result.error}`);
        } else {
          throw new Error(result.error || 'Action failed on server');
        }
      } else {
        console.log(`✅ [PokerTable] Action ${action} processed via secure API`);
      }

      setRaiseAmount(0);

    } catch (err: any) {
      console.error('Action failed:', err);
      //  ENTERPRISE: Only show error if it's not a race condition

      if (!err.message?.includes('not in progress') && !err.message?.includes('state has changed')) {
        setActionError(err.message || 'Action failed');
        playActionSound('error', soundEnabled);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // ⚡ PRE-ACTIONS: reset every new hand, and fire the armed action the
  // moment the turn arrives. Pre-check is invalidated by a bet (control
  // returns to the player instead of auto-checking into a raise).
  useEffect(() => { setPreAction(null); }, [gameState.handNumber]);

  useEffect(() => {
    if (!preAction || !canTakeAction || isProcessing) return;
    const facingBet = callAmount > 0;
    let action: 'fold' | 'check' | 'call' | null = null;
    if (preAction === 'checkFold') action = facingBet ? 'fold' : 'check';
    else if (preAction === 'check') action = facingBet ? null : 'check';
    else if (preAction === 'callAny') action = facingBet ? 'call' : 'check';
    setPreAction(null);
    if (action) handlePlayerAction(action);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preAction, canTakeAction, isProcessing, callAmount]);

  // Get suit symbol
  const getSuitSymbol = (suit: string): string => {
    const suits: { [key: string]: string } = {
      hearts: '♥',

      diamonds: '♦',

      clubs: '♣',

      spades: '♠',

    };
    return suits[suit] || '?';
  };

  // Get suit color class
  const getSuitColorClass = (suit: string): string => {
    return suit === 'hearts' || suit === 'diamonds' ? 'red' : 'black';
  };

  //  Check if hole cards are valid (not hidden placeholder cards)

  const hasValidCards = (cards: any[] | undefined): boolean => {
    if (!cards || cards.length < 2) return false;
    // Cards are hidden if rank is empty string
    return cards[0]?.rank && cards[0].rank !== '' && cards[1]?.rank && cards[1].rank !== '';
  };

  // Get visible community cards based on round (or runout animation)
  //  MOVED: Must be defined before getHandStrength which uses it

  const visibleCommunityCards = useMemo(() => {
    //  DEBUG: ALWAYS log what's happening

    console.log('🃏 [CARDS] round:', gameState.round, '| communityCards:', gameState.communityCards?.length || 0, '| cards:', JSON.stringify(gameState.communityCards));
    
    //  During all-in runout, let runout control visibility

    if (runoutVisibleCards !== null && runoutPhase !== 'none' && runoutPhase !== 'complete') {
      console.log('🃏 [CARDS] Runout controlling - showing:', runoutVisibleCards.length);
      return runoutVisibleCards;
    }
    
    //  SIMPLE: Just show cards based on round - NO FANCY LOGIC

    const round = gameState.round;
    const cards = gameState.communityCards || [];
    
    if (round === 'preflop') {
      console.log('🃏 [CARDS] PREFLOP - 0 cards');
      return [];
    }
    if (round === 'flop') {
      const visible = cards.slice(0, 3);
      console.log('🃏 [CARDS] FLOP - 3 cards:', visible);
      return visible;
    }
    if (round === 'turn') {
      const visible = cards.slice(0, 4);
      console.log('🃏 [CARDS] TURN - 4 cards:', visible);
      return visible;
    }
    if (round === 'river') {
      const visible = cards.slice(0, 5);
      console.log('🃏 [CARDS] RIVER - 5 cards:', visible);
      return visible;
    }
    
    // Showdown/ended - show all
    console.log('🃏 [CARDS] SHOWDOWN/OTHER - all cards:', cards.length);
    return cards;
  }, [gameState.communityCards, gameState.round, runoutVisibleCards, runoutPhase]);

  // Get hand strength based on VISIBLE community cards (not all 5)
  const getHandStrength = useCallback(() => {
    if (!currentPlayer || !currentPlayer.holeCards || currentPlayer.holeCards.length === 0) return '';
    
    //  Don't calculate if cards are hidden placeholders

    if (!hasValidCards(currentPlayer.holeCards)) return '';
    
    //  Don't show hand strength during all-in runout card reveal phase

    // Only show once we start revealing the board (flop onwards)
    if (runoutPhase === 'reveal-cards') {
      return ''; // Hide during hole card reveals
    }
    
    //  FIXED: Use visible community cards, not all cards

    // This prevents spoiling the hand strength before runout animation shows the cards
    const hand = gameEngine.evaluateHand(currentPlayer.holeCards, visibleCommunityCards);
    return hand?.rank || '';
  }, [currentPlayer, visibleCommunityCards, runoutPhase]);

  // Get round announcement
  const getRoundAnnouncement = () => {
    if (gameState.round === 'preflop') return 'A Flop is Coming...';
    if (gameState.round === 'flop') return 'Turn Card Coming...';
    if (gameState.round === 'turn') return 'River Card Coming...';
    return null;
  };

  //  Show round announcement briefly when round changes

  useEffect(() => {
    // Only show for rounds that have announcements
    if (gameState.round === 'preflop' || gameState.round === 'flop' || gameState.round === 'turn') {
      setShowRoundAnnouncement(true);
      
      // Hide after 2.5 seconds
      const timer = setTimeout(() => {
        setShowRoundAnnouncement(false);
      }, 2500);
      
      return () => clearTimeout(timer);
    } else {
      setShowRoundAnnouncement(false);
    }
  }, [gameState.round]);

  //  Track previous player index and actions for announcements

  const prevPlayerIndexRef = useRef<number | null>(null);
  const prevActionsRef = useRef<{ [playerId: string]: string }>({});

  //  Voice announcement: "Your turn"

  useEffect(() => {
    const prevIndex = prevPlayerIndexRef.current;
    const currentIndex = gameState.currentPlayerIndex;
    
    // Only announce when turn changes TO the current player
    if (prevIndex !== currentIndex && gameState.status === 'playing' && gameState.round !== 'ended') {
      prevPlayerIndexRef.current = currentIndex;
      
      const turnPlayer = safePlayers[currentIndex];
      if (turnPlayer && turnPlayer.id === currentUserId && !turnPlayer.folded && !turnPlayer.allIn) {
        voiceAnnouncer.announceYourTurn();
      }
    }
  }, [gameState.currentPlayerIndex, gameState.status, gameState.round, gameState.players, currentUserId]);

  //  Voice announcement for OTHER players' actions (not just your own)

  useEffect(() => {
    if (gameState.status !== 'playing' || gameState.round === 'ended') return;
    
    safePlayers.forEach(player => {
      if (!player || !player.id) return; //  Extra null guard

      const prevAction = prevActionsRef.current[player.id];
      const currentAction = player.lastAction;
      
      // Only announce if action changed and it's NOT the current user (we announce our own in handlePlayerAction)
      if (currentAction && currentAction !== prevAction && player.id !== currentUserId) {
        const playerName = getPlayerDisplayName(player.id);
        
        switch (currentAction) {
          case 'fold':
            voiceAnnouncer.announceFold(playerName);
            break;
          case 'check':
            voiceAnnouncer.announceCheck(playerName);
            break;
          case 'call':
            voiceAnnouncer.announceCall(playerName);
            break;
          case 'raise':
            voiceAnnouncer.announceRaise(playerName);
            break;
          case 'all-in':
            voiceAnnouncer.announceAllIn(playerName);
            break;
        }
      }
      
      prevActionsRef.current[player.id] = currentAction || '';
    });
  }, [safePlayers, gameState.status, gameState.round, currentUserId, usernames]);

  //  Voice announcements for round changes

  useEffect(() => {
    const prevRound = prevRoundRef.current;
    const currentRound = gameState.round;
    
    // Only announce if round actually changed
    if (prevRound !== currentRound) {
      prevRoundRef.current = currentRound;
      
      if (currentRound === 'flop') {
        voiceAnnouncer.announceFlop();
      } else if (currentRound === 'turn') {
        voiceAnnouncer.announceTurn();
      } else if (currentRound === 'river') {
        voiceAnnouncer.announceRiver();
      } else if (currentRound === 'showdown') {
        voiceAnnouncer.announceShowdown();
      } else if (currentRound === 'preflop' && prevRound === 'ended') {
        // New hand starting
        voiceAnnouncer.announceNewHand(gameState.handNumber);
      }
    }
  }, [gameState.round, gameState.handNumber]);

//  Voice announcement for winner

//  RUNOUT: Wait for animation to complete before announcing

  useEffect(() => {
    // Don't process if round isn't ended yet
    if (gameState.round !== 'ended' || !gameState.winners || gameState.winners.length === 0) {
      return;
    }
    
    //  CRITICAL: Check if this is a fold-win or all-in showdown

    const nonFoldedCount = safePlayers.filter(p => inShowdownHand(p)).length;
    const isFoldWin = gameState.handResult?.allOthersFolded === true;
    
    //  FOLD-WIN FAST PATH: Announce immediately

    if (!isFoldWin) {
      // Only wait for runout if: multiple players remaining (not fold win)
      const needsRunout = nonFoldedCount >= 2;
      if (needsRunout && runoutPhase !== 'complete') {
        return; // Silent return - winner effect already logs
      }
    }
    
    // ========================================================================
    //  ENTERPRISE SPLIT POT DETECTION (same as toast notification)

    // ========================================================================
    // Uncalled-bet returns ride in winners[] flagged `uncalled` - they are
    // a player's own chips coming back, never a win. Run-it-twice rows come
    // one per run per winner; merge them per player so the notice judges
    // the whole hand (a chopped run and a lost run can still be a loss).
    const winners = aggregateWinnerRows((gameState.winners || []).filter((w: any) => !w.uncalled));
    const hasMultipleWinners = winners.length > 1;
    const isRunItTwice = winners.some((w: any) => w.run !== undefined);
    
    // True split = all winners have identical hand AND not RIT
    const isTrueSplitPot = hasMultipleWinners && 
      !isRunItTwice && 
      winners.every((w: any) => w.hand === winners[0].hand);
    
    const currentUserWin = winners.find((w: any) => w.playerId === currentUserId);
    const primaryWinner = [...winners].sort((a: any, b: any) => (b.amount || 0) - (a.amount || 0))[0];
    
    // Use first winner's ID + hand number for dedup
    const announcementKey = `${primaryWinner.playerId}-${gameState.handNumber}`;
    
    // Only announce once per hand
    if (prevWinnerRef.current !== announcementKey) {
      prevWinnerRef.current = announcementKey;
      
      if (isTrueSplitPot) {
        //  TRUE SPLIT POT ANNOUNCEMENT

        const winnerNames = winners
          .filter((w: any) => w.playerId !== currentUserId)
          .map((w: any) => getPlayerDisplayName(w.playerId));
        voiceAnnouncer.announceSplitPot(!!currentUserWin, winnerNames);
      } else {
        // Single winner OR side pot - announce primary winner
        const winnerName = getPlayerDisplayName(primaryWinner.playerId);
        const isCurrentUserWinner = primaryWinner.playerId === currentUserId;
        
        //  Get hand description (e.g., "a flush", "two pair")

        const handDescription = primaryWinner.hand || primaryWinner.handName || null;
        
        // Announce winner with hand description (only winner hears "chicken dinner")
        voiceAnnouncer.announceWinner(winnerName, isCurrentUserWinner, handDescription, isFoldWin);
      }
      
      // Announce big pots
      const totalPot = winners.reduce((sum: number, w: any) => sum + (w.amount || 0), 0);
      if (totalPot >= 50) {
        setTimeout(() => voiceAnnouncer.announceBigPot(totalPot), 2000);
      }
    }
  }, [gameState.round, gameState.winners, gameState.players, gameState.handResult, currentUserId, usernames, runoutPhase]);

  //  Reset refs when new hand starts

  useEffect(() => {
    if (gameState.round === 'preflop') {
      prevWinnerRef.current = null;
      prevActionsRef.current = {}; // Reset action tracking for new hand
      //  Reset runout state for new hand

      runoutActiveRef.current = false;
      runoutAnimationStartRef.current = 0; //  Clear start time

      setRunoutPhase('none');
      setVisibleBoardCount(0);
      setPlayerEquities({});
      //  Reset RIT state for new hand

      ritActiveRef.current = false;
      ritAnimationStartRef.current = 0; //  Clear start time

      ritPlayersRef.current = []; //  Clear captured players

      setRitPhase('none');
      setRitRunsVisible(0);
      setRitPartialCards(0);
      setRitLiveEquity({});
      setRitRun1VisibleCards(0);
      setRitRun2VisibleCards(0);
      setRitRun1Equity({});
      setRitRun2Equity({});
      setRitShowRun1Winner(false);
      setRitShowRun2Winner(false);
    }
  }, [gameState.round]);

  //  UPDATED: Render player seat with showdown card reveal

  const renderPlayerSeat = (seatIndex: number, position: string) => {
    const player = getPlayerAtSeat(seatIndex);
    const isCurrentUser = player?.id === currentUserId;
    const isPlayerTurn = player && safePlayers.findIndex(p => p.id === player.id) === gameState.currentPlayerIndex;
    const isWinner = gameState.winners?.some(w => w.playerId === player?.id);
    const winAmount = gameState.winners?.find(w => w.playerId === player?.id)?.amount;
    const isDealer = player?.seat === gameState.dealerButton;
    const isSB = player?.seat === gameState.smallBlindIndex;
    const isBB = player?.seat === gameState.bigBlindIndex;

    //  Determine if we should show this player's cards

    const isShowdown = gameState.round === 'ended' || gameState.round === 'showdown';
    const winnerChoseToShow = isWinner && isFoldWin && (showCardsChosen || gameState.handResult?.winnerShowedCards);
    const shouldRevealCards = player && isShowdown && (
      // Normal showdown - reveal non-folded players
      (!isFoldWin && !player.folded) ||
      // Fold win - only reveal if winner chose to show
      winnerChoseToShow
    );

    //  Get player index in allInShowdownPlayers for runout sequence

    const runoutPlayerIndex = player ? allInShowdownPlayers.findIndex(p => p.id === player.id) : -1;
    const isInRunout = runoutPlayerIndex >= 0 && (runoutPhase !== 'none' || (ritPhase !== 'none' && ritPhase !== 'complete'));
    
    //  Get equity for this player

    const playerEquity = player ? equitiesForSVG[player.id] : undefined;
    const showEquity = isInRunout && playerEquity !== undefined && runoutPhase !== 'reveal-cards' && ritPhase !== 'reveal-cards';

    if (!player) {
      return (
        <div 
          key={seatIndex} 
          className={`player-seat ${position} empty`}
          onClick={() => handleEmptySeatClick(seatIndex)}
          title="Join Table"
          style={{ cursor: 'pointer' }}
        >
          <div className="empty-seat-circle">
            <span>+</span>
          </div>
        </div>
      );
    }

    return (
      <div 
        key={seatIndex} 
        className={`player-seat ${position} ${isPlayerTurn ? 'active-turn' : ''} ${player.folded ? 'folded' : ''} ${isWinner ? 'winner' : ''}`}
      >
        {/* Face-down cards behind avatar (during play, not for current user) */}
        {!player.folded && player.holeCards && player.holeCards.length > 0 && !isCurrentUser && !shouldRevealCards && !isInRunout && (
          <div className="player-hole-cards">
            <div className="mini-card back"></div>
            <div className="mini-card back"></div>
          </div>
        )}

        {/*  RUNOUT SUSPENSE: Show cards one at a time with flip animation */}

        {isInRunout && hasValidCards(player.holeCards) && (
          <div className="player-hole-cards revealed runout-reveal">
            {player.holeCards.map((card, cardIdx) => {
              const isThisCardRevealed = isCardRevealed(runoutPlayerIndex, cardIdx);
              return isThisCardRevealed ? (
                <div key={cardIdx} className={`mini-card ${getSuitColorClass(card.suit)} flip-in`}>
                  <span className="mini-rank">{card.rank}</span>
                  <span className="mini-suit">{getSuitSymbol(card.suit)}</span>
                </div>
              ) : (
                <div key={cardIdx} className="mini-card back"></div>
              );
            })}
          </div>
        )}

        {/*  Show actual cards at showdown (non-runout) - only if cards are valid */}

        {shouldRevealCards && !isInRunout && hasValidCards(player.holeCards) && (
          <div className="player-hole-cards revealed">
            {player.holeCards.map((card, idx) => (
              <div key={idx} className={`mini-card ${getSuitColorClass(card.suit)}`}>
                <span className="mini-rank">{card.rank}</span>
                <span className="mini-suit">{getSuitSymbol(card.suit)}</span>
              </div>
            ))}
          </div>
        )}

        {/* ⏱ TURN TIMER - Circular countdown ring */}

        {isPlayerTurn && !player.folded && !player.allIn && gameState.status === 'playing' && gameState.round !== 'ended' && gameState.round !== 'showdown' && (
          <svg className="turn-timer-ring" viewBox="0 0 100 100">
            <circle
              className="timer-bg"
              cx="50" cy="50" r="45"
              fill="none"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="4"
            />
            <circle
              className={`timer-progress ${turnTimeLeft <= 10 ? 'warning' : ''}`}
              cx="50" cy="50" r="45"
              fill="none"
              stroke={turnTimeLeft <= 10 ? '#ff4444' : '#4CAF50'}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={`${(turnTimeLeft / TURN_DURATION) * 283} 283`}
              transform="rotate(-90 50 50)"
            />
            <text 
              x="50" y="55" 
              textAnchor="middle" 
              className={`timer-text ${turnTimeLeft <= 10 ? 'warning' : ''}`}
              fill={turnTimeLeft <= 10 ? '#ff4444' : '#fff'}
              fontSize="18"
              fontWeight="bold"
            >
              {turnTimeLeft}
            </text>
          </svg>
        )}

        {/* Avatar circle */}
        <div className={`player-avatar-circle ${isPlayerTurn ? 'turn-glow' : ''} ${isWinner ? 'winner-glow' : ''}`}>
          {player.folded ? (
            <div className="fold-overlay">
              <span className="fold-x"></span>

              <span className="fold-text">FOLD</span>
            </div>
          ) : (
            <div className="avatar-letter">
              {getPlayerDisplayName(player.id).charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/*  EQUITY BADGE - Win percentage during runout */}

        {showEquity && (
          <div className={`equity-badge ${playerEquity >= 50 ? 'favorite' : 'underdog'}`}>
            {playerEquity}%
          </div>
        )}

        {/* Turn indicator badge */}
        {isPlayerTurn && !player.folded && (
          <div className="turn-badge">TURN</div>
        )}

        {/*  Hand type badge for winner - use THIS player's winner entry */}

        {isWinner && (() => {
          const playerWinEntry = gameState.winners?.find((w: any) => w.playerId === player?.id);
          return playerWinEntry?.hand && playerWinEntry.hand !== 'Fold Win' && !isFoldWin ? (
            <div className="hand-type-badge">{playerWinEntry.hand}</div>
          ) : null;
        })()}

        {/*  WINNER badge for fold wins */}

        {isWinner && isFoldWin && (
          <div className="hand-type-badge winner-badge"> WINNER</div>

        )}

        {/* Player info card */}
        <div
          className={`player-info-card ${isCurrentUser ? 'current-user' : ''} ${isWinner ? 'winner-card' : ''}`}
          style={{ position: 'relative' }}
        >
          {/* BOT disclosure - positioned on the card corner, NOT inline in
              the name row: that row clips at 80px with ellipsis, so an
              inline tag vanished on any bot with a long name. */}
          {String(player.id).startsWith('house_bot_') && (
            <span style={{
              position: 'absolute', top: -8, right: -7, zIndex: 3,
              fontSize: 8, fontWeight: 800, letterSpacing: 1,
              color: '#4ade80', background: '#0d3b2e', border: '1px solid #22c55e',
              borderRadius: 7, padding: '1px 5px',
            }}>BOT</span>
          )}
          {/* Trophy count */}
          <div className="trophy-row">
            <span className="trophy-icon"></span>

            <span className="trophy-count">0</span>
            <span className="trophy-games">(0)</span>
          </div>

          {/* Player name */}
          <div className="player-name-row">
            {getPlayerDisplayName(player.id)}
          </div>
          
          {/* Chips + win amount */}
          <div className="chips-row">
            <span className="chip-amount">{player.chips.toFixed(2)}</span>
            {isWinner && winAmount && (
              <span className="win-amount">+{winAmount.toFixed(2)}</span>
            )}
          </div>
        </div>

        {/* Bet chip (if player has bet) */}
        {player.currentBet > 0 && (
          <div className="bet-chip-display">
            <div className="bet-chip-circle">{player.currentBet.toFixed(2)}</div>
          </div>
        )}

        {/* Position chips (Dealer, SB, BB) */}
        {isDealer && <div className="position-chip dealer">DEALER</div>}
        {isSB && !isDealer && <div className="position-chip sb">SB</div>}
        {isBB && <div className="position-chip bb">BB</div>}

        {/* Last action indicator */}
        {player.lastAction && !player.folded && (
          <div className="last-action-bubble">
            {player.lastAction === 'check' && 'Check'}
            {player.lastAction === 'call' && 'Call'}
            {player.lastAction === 'raise' && 'Raise'}
            {player.lastAction === 'all-in' && 'All-In!'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="poker-table-wrapper" id="poker-table-container">
      {/*  WINNER CELEBRATION - Confetti & Animation */}

      <WinnerCelebration
        isWinner={showCelebration}
        amount={celebrationAmount}
        currency={gameState.currency || 'playcoins'}
        handDescription={celebrationHandDesc}
        loserHandDescription={celebrationLoserDesc}
        isFoldWin={celebrationIsFoldWin}
        onComplete={() => setShowCelebration(false)}
      />

      {/*  STORIES: Share Win Button - Shows after big win */}

      {showShareWinButton && shareWinData && (
        <div
          style={{
            position: 'absolute',
            top: '120px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 200,
            animation: 'bountyPop 0.3s ease-out',
          }}
        >
          <button
            onClick={() => {
              setShowShareWinButton(false);
              setShowShareWinModal(true);
            }}
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: '#000',
              padding: '12px 24px',
              borderRadius: '25px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px',
              boxShadow: '0 4px 20px rgba(245, 158, 11, 0.5)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
             Share This Win!

          </button>
        </div>
      )}

      {/*  STORIES: Share Win Modal */}

      <ShareWinModal
        isOpen={showShareWinModal}
        onClose={() => {
          setShowShareWinModal(false);
          setShareWinData(null);
        }}
        userId={currentUserId}
        username={usernames[currentUserId] || auth.currentUser?.displayName || 'Player'}
        avatarUrl={userPhotos[currentUserId] || auth.currentUser?.photoURL || undefined}
        potAmount={shareWinData?.potAmount || 0}
        currency={(gameState.currency || 'playcoins') as 'pkcash' | 'playcoins'}
        handType={shareWinData?.handType}
        tableName={tableName}
        opponents={shareWinData?.opponents}
        wasShowdown={shareWinData?.wasShowdown}
      />

      {/*  REMOVED: ShowdownDisplay modal - Winner now shown on table */}


      {/* Notification (toast style, doesn't block view) */}
      {showNotification && notificationData && (
        <GameResultNotification
          type={notificationData.type}
          title={notificationData.title}
          message={notificationData.message}
          handName={notificationData.handName}
          detailLines={notificationData.detailLines}
          onClose={() => setShowNotification(false)}
        />
      )}

      {/* Round Announcement Banner - shows briefly at start of each round */}
      {showRoundAnnouncement && getRoundAnnouncement() && gameState.status === 'playing' && (
        <div className="round-announcement">
          {getRoundAnnouncement()}
        </div>
      )}

      {/*  BOMB POT ANNOUNCEMENT - Small banner at top, doesn't block cards */}

      {showBombPotAnnouncement && (
        <div
          style={{
            position: 'absolute',
            top: '60px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 150,
            background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
            color: '#fff',
            padding: '10px 25px',
            borderRadius: '25px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(239, 68, 68, 0.6)',
            animation: 'bountyPop 0.3s ease-out',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <span style={{ fontSize: '24px' }}></span>

          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
            BOMB POT! Everyone antes {gameState.bombPotAnte || '5x BB'}
          </span>
        </div>
      )}

      {/*  7-2 BOUNTY WIN CELEBRATION */}

      {gameState.sevenTwoBountyResult && gameState.round === 'ended' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 200,
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: '#000',
            padding: '30px 50px',
            borderRadius: '20px',
            textAlign: 'center',
            boxShadow: '0 0 60px rgba(245, 158, 11, 0.6)',
            animation: 'bountyPop 0.5s ease-out',
          }}
        >
          <div style={{ fontSize: '50px', marginBottom: '10px' }}></div>

          <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
            7-2 BOUNTY!
          </div>
          <div style={{ fontSize: '18px' }}>
            {usernames[gameState.sevenTwoBountyResult.winnerId] || 'Player'} wins!
          </div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', marginTop: '8px' }}>
            +${gameState.sevenTwoBountyResult.totalBounty}
          </div>
        </div>
      )}

      {/*  LUCKY POT MULTIPLIER CELEBRATION */}

      {/* Belt to the timer fix: whatever happens, never render the Lucky Pot
          card over a live hand - the round guard kills it the moment the
          next deal lands. */}
      {showLuckyPot && luckyPotData && gameState.round === 'ended' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 250,
            background: luckyPotData.tier === 'mega' 
              ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 25%, #ec4899 50%, #8b5cf6 75%, #3b82f6 100%)'
              : luckyPotData.tier === 'super'
              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #fbbf24 100%)'
              : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            color: luckyPotData.tier === 'mega' ? '#fff' : '#000',
            padding: luckyPotData.tier === 'mega' ? '40px 60px' : '30px 50px',
            borderRadius: '20px',
            textAlign: 'center',
            boxShadow: luckyPotData.tier === 'mega' 
              ? '0 0 80px rgba(251, 191, 36, 0.8), 0 0 120px rgba(139, 92, 246, 0.6)'
              : luckyPotData.tier === 'super'
              ? '0 0 60px rgba(139, 92, 246, 0.7)'
              : '0 0 40px rgba(251, 191, 36, 0.6)',
            animation: 'bountyPop 0.5s ease-out',
          }}
        >
          <div style={{ 
            fontSize: luckyPotData.tier === 'mega' ? '60px' : '50px', 
            marginBottom: '10px',
            animation: luckyPotData.tier === 'mega' ? 'pulse 0.5s infinite' : 'none',
          }}>
            {luckyPotData.tier === 'mega' ? '' : luckyPotData.tier === 'super' ? '' : ''}

          </div>
          <div style={{ 
            fontSize: luckyPotData.tier === 'mega' ? '28px' : '24px', 
            fontWeight: 'bold', 
            marginBottom: '8px',
            textShadow: luckyPotData.tier === 'mega' ? '0 0 10px rgba(255,255,255,0.8)' : 'none',
          }}>
            {luckyPotData.tier === 'mega' ? 'MEGA LUCKY POT!' :
             luckyPotData.tier === 'super' ? 'SUPER LUCKY POT!' : 'LUCKY POT!'}
          </div>
          <div style={{ 
            fontSize: luckyPotData.tier === 'mega' ? '36px' : '28px', 
            fontWeight: 'bold',
            color: luckyPotData.tier === 'mega' ? '#fef08a' : '#000',
          }}>
            {luckyPotData.multiplier}x MULTIPLIER
          </div>
          <div style={{ 
            fontSize: '20px', 
            marginTop: '12px',
            fontWeight: 'bold',
          }}>
            +{Math.round(luckyPotData.totalBonus).toLocaleString()} Bonus PlayCoins!
          </div>
          {luckyPotData.tier === 'mega' && (
            <div style={{ marginTop: '10px', fontSize: '30px' }}>
                  

            </div>
          )}
        </div>
      )}

      {/* RUN IT MULTIPLE TIMES renders ON THE FELT: PokerTableSVG draws each
          run as its own board row (run 1 locks in with its winner, run 2 deals
          beneath it) and the seat badges carry the live percentages. The old
          centre pop-up and the results card are gone; the win/lose notice
          and the felt itself tell the story. */}


      {/* ⏱ GLOBAL TURN TIMER - Always visible at top during gameplay */}

      {gameState.status === 'playing' && 
       gameState.round !== 'ended' && 
       gameState.round !== 'showdown' && 
       currentTurnPlayer &&
       !currentTurnPlayer.folded &&
       !currentTurnPlayer.allIn && (
        <div 
          className="global-turn-timer"
          style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 20px',
            borderRadius: '25px',
            background: turnTimeLeft <= 10 
              ? 'linear-gradient(135deg, rgba(255,0,0,0.9), rgba(200,0,0,0.9))' 
              : turnTimeLeft <= 20
              ? 'linear-gradient(135deg, rgba(255,170,0,0.9), rgba(200,130,0,0.9))'
              : 'linear-gradient(135deg, rgba(0,100,0,0.9), rgba(0,80,0,0.9))',
            boxShadow: turnTimeLeft <= 10 
              ? '0 0 20px rgba(255,0,0,0.8), 0 4px 15px rgba(0,0,0,0.5)' 
              : '0 4px 15px rgba(0,0,0,0.5)',
            animation: turnTimeLeft <= 10 ? 'pulse 0.5s ease-in-out infinite' : 'none',
          }}
        >
          {/* Timer circle */}
          <svg width="40" height="40" viewBox="0 0 40 40">
            {/* Background circle */}
            <circle
              cx="20"
              cy="20"
              r="16"
              fill="none"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="4"
            />
            {/* Progress circle */}
            <circle
              cx="20"
              cy="20"
              r="16"
              fill="none"
              stroke="#fff"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={`${(turnTimeLeft / TURN_DURATION) * 100.5} 100.5`}
              transform="rotate(-90 20 20)"
              style={{ transition: 'stroke-dasharray 1s linear' }}
            />
            {/* Center text */}
            <text
              x="20"
              y="24"
              textAnchor="middle"
              fill="#fff"
              fontSize="12"
              fontWeight="bold"
            >
              {turnTimeLeft}
            </text>
          </svg>
          
          {/* Player name */}
          <span style={{ 
            color: '#fff', 
            fontWeight: 'bold', 
            fontSize: '14px',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
          }}>
            {isCurrentPlayerTurn ? ' YOUR TURN' : `${getPlayerDisplayName(currentTurnPlayer?.id || '')}'s turn`}

          </span>
        </div>
      )}

      {/* Main Table Area - Using SVG Design */}
      <div className="table-area">
        <PokerTableSVG
          players={playersForSVG}
          currentPlayerIndex={gameState.currentPlayerIndex}
          pot={isRunoutInProgress && cachedPot > 0 ? cachedPot : gameState.pot}
          communityCards={visibleCommunityCards} //  Use controlled visible cards for runout suspense

          currentUserId={currentUserId}
          round={gameState.round}
          handResult={handResultForSVG}
          onShowCards={handleShowCards}
          onSeatClick={onSeatClick}
          maxSeats={maxSeats}
          onPlayerPositionsUpdate={handlePlayerPositionsUpdate}
          disableCardFiltering={runoutPhase !== 'none' || ritPhase !== 'none'} //  Tell SVG to use cards as-is during runout or RIT

          turnTimeLeft={turnTimeLeft}
          turnDuration={TURN_DURATION}
          playerEquities={equitiesForSVG}
          ritBoards={ritBoardsForSVG}
          runoutPhase={runoutPhase}
          ritPhase={ritPhase}
          allInShowdown={gameState.allInShowdown === true}
          allInOccurred={gameState.allInOccurred === true}
          centerInfo={centerInfo}
          leaderBadges={leaderBadges}
          onPlayerClick={onPlayerClick}
          seatTags={seatTags}
          hitPlayerId={hitPlayerId}
          dealerPosition={gameState.dealerButton}
          smallBlindIndex={gameState.sbIndex}
          bigBlindIndex={gameState.bbIndex}
        />
        
        {/*  ANIMATION LAYER - Fixed position for visibility */}

        {/*  ENTERPRISE: Use MemoizedAnimation to prevent render spam */}

        {activeAnimations.length > 0 && activeAnimations.map((animation) => (
          <MemoizedAnimation
            key={animation.id}
            animation={animation}
            senderPosition={playerPositions[animation.senderId]}
            targetPosition={playerPositions[animation.targetId]}
            onImpact={(tid) => {
              setHitPlayerId(tid);
              if (hitTimerRef.current) clearTimeout(hitTimerRef.current);
              hitTimerRef.current = setTimeout(() => setHitPlayerId(null), 900);
            }}
          />
        ))}
      </div>

      {/* Your Hand Display (for current player) - NOT for waiting/rebuy players */}
      {/*  Only show if cards are valid (fetched from secure storage) */}

      {currentPlayer && hasValidCards(currentPlayer.holeCards) &&
       !currentPlayer.folded && !currentPlayer.waitingForNextHand && !currentPlayer.needsRebuy &&
       gameState.round !== 'waiting' && (
        <div className="your-hand-section">
          <div className="your-cards">
            {/*  Safety: Only show first 2 cards (hole cards) */}

            {currentPlayer.holeCards.slice(0, 2).map((card, idx) => (
              <div key={idx} className={`your-card ${getSuitColorClass(card.suit)}`}>
                <div className="card-corner top-left">
                  <span className="rank">{card.rank}</span>
                  <span className="suit">{getSuitSymbol(card.suit)}</span>
                </div>
                <div className="card-center-suit">{getSuitSymbol(card.suit)}</div>
                <div className="card-corner bottom-right">
                  <span className="rank">{card.rank}</span>
                  <span className="suit">{getSuitSymbol(card.suit)}</span>
                </div>
              </div>
            ))}
          </div>
          {getHandStrength() && (
            <div className="hand-strength-badge">{getHandStrength()}</div>
          )}
        </div>
      )}

      {/* 🪑 JUST SEATED - arrived mid-hand (table move, rebuy, late reg).
          Standard rules: watch this hand from the rail seat - no cards, no
          blinds, stack untouched - and get dealt in when the next hand
          starts. Say so, or the wait reads as a bug. */}
      {currentPlayer && (currentPlayer as any).waitingForNextHand === true
        && !currentPlayer.eliminated
        && gameState.status === 'playing' && gameState.round
        && gameState.round !== 'ended' && gameState.round !== 'waiting' && (
        <div style={{
          position: 'fixed', bottom: '132px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 1300, textAlign: 'center', maxWidth: '94vw',
          background: 'rgba(13, 31, 46, 0.96)', border: '1px solid rgba(34, 211, 238, 0.55)',
          borderRadius: '22px', padding: '10px 18px', boxShadow: '0 8px 30px rgba(0,0,0,0.55)',
        }}>
          <span style={{ color: '#7dd3fc', fontSize: 'clamp(11.5px, 3.2vw, 13px)', fontWeight: 700, letterSpacing: '0.04em' }}>
            SEATED - you'll be dealt in next hand. Watching this one; no blinds are taken while you wait.
          </span>
        </div>
      )}

      {/* 😴 SITTING OUT - shown while my seat is away. Hands keep being
          dealt (tournaments auto-check/fold in seconds); JOIN NOW resumes. */}
      {currentPlayer && (currentPlayer as any).isAway === true && !currentPlayer.eliminated && (
        <div style={{
          position: 'fixed', bottom: '84px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 1300, display: 'flex', alignItems: 'center', gap: '10px',
          flexWrap: 'wrap', justifyContent: 'center', maxWidth: '94vw',
          background: 'rgba(13, 31, 46, 0.96)', border: '1px solid rgba(212, 175, 55, 0.6)',
          borderRadius: '22px', padding: '10px 16px', boxShadow: '0 8px 30px rgba(0,0,0,0.55)',
        }}>
          <span style={{ color: '#fde68a', fontSize: 'clamp(11.5px, 3.2vw, 13px)', fontWeight: 700, letterSpacing: '0.04em' }}>
            SITTING OUT - your hands check or fold automatically
          </span>
          <button
            onClick={() => { gameApiService.imBack(tableId).catch(() => {/* noop */}); }}
            style={{
              background: 'linear-gradient(135deg, #f5d976, #d4af37 60%, #a97f1f)',
              border: 'none', color: '#181205', borderRadius: '16px',
              padding: '8px 18px', fontWeight: 800, fontSize: '12px',
              letterSpacing: '0.1em', cursor: 'pointer',
            }}
          >
            JOIN NOW
          </button>
        </div>
      )}

      {/* ⚡ PRE-ACTION BAR - shown while waiting for the turn: arm an action
          and it fires automatically when the turn arrives (CoinPoker-style) */}
      {!canTakeAction && currentPlayer && !currentPlayer.folded && !currentPlayer.allIn
        && !currentPlayer.eliminated && !currentPlayer.waitingForNextHand && !currentPlayer.needsRebuy
        && gameState.status === 'playing'
        && !!gameState.round && gameState.round !== 'ended'
        && gameState.round !== 'waiting' && gameState.round !== 'showdown' && (
        <div style={{
          position: 'fixed', bottom: '18px', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: '8px', zIndex: 1100,
        }}>
          {([
            { key: 'checkFold', label: 'CHECK / FOLD' },
            { key: 'check', label: 'CHECK' },
            { key: 'callAny', label: 'CALL ANY' },
          ] as const).map(opt => (
            <button
              key={opt.key}
              onClick={() => setPreAction(p => (p === opt.key ? null : opt.key))}
              style={{
                padding: '10px 16px', borderRadius: '18px', fontSize: '12px', fontWeight: 700,
                cursor: 'pointer', letterSpacing: '0.4px',
                border: preAction === opt.key ? '2px solid #22d3ee' : '1px solid rgba(255,255,255,0.25)',
                background: preAction === opt.key ? 'rgba(8, 145, 178, 0.9)' : 'rgba(15, 23, 42, 0.85)',
                color: preAction === opt.key ? '#fff' : '#cbd5e1',
                boxShadow: preAction === opt.key ? '0 0 12px rgba(34, 211, 238, 0.45)' : 'none',
              }}
            >
              {preAction === opt.key ? '✓ ' : ''}{opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Action Buttons (ReBet style) - NOT for waiting/rebuy players */}
      {/*  ENTERPRISE: Use canTakeAction guard for comprehensive validation */}

      {canTakeAction && (
        <div className="action-buttons-bar">
          {/* ⏱ +10s Time Bank Button */}

          {!timeBankUsed && turnTimeLeft <= 15 && (
            <button 
              className="action-btn timebank-btn"
              onClick={handleUseTimeBank}
              disabled={isProcessing}
              title="Add 10 seconds (one-time use per hand)"
            >
              +10s
            </button>
          )}

          {/* Call/Check Button */}
          {possibleActions.includes('check') && (
            <button 
              className="action-btn check-btn"
              onClick={() => handlePlayerAction('check')}
              disabled={isProcessing}
              data-action="check"
            >
              CHECK
            </button>
          )}
          
          {possibleActions.includes('call') && (
            <button 
              className="action-btn call-btn"
              onClick={() => handlePlayerAction('call')}
              disabled={isProcessing}
              data-action="call"
            >
              CALL {callAmount.toFixed(2)}
            </button>
          )}

          {/* Raise Button */}
          {possibleActions.includes('raise') && (
            <button
              className="action-btn raise-btn"
              onClick={() => {
                if (!showRaiseSlider && currentPlayer
                    && raiseAmount < Math.min(minRaise, currentPlayer.chips)) {
                  setRaiseAmount(Math.min(minRaise, currentPlayer.chips));
                }
                setShowRaiseSlider(!showRaiseSlider);
              }}
              disabled={isProcessing}
            >
              RAISE
            </button>
          )}

          {/* All-In Button - always available when the player has chips.
              (It used to hide unless AoF mode or short-stacked, which left
              tournament tables looking like they had only two options.) */}
          {possibleActions.includes('all-in') && (
            <button 
              className="action-btn allin-btn"
              onClick={() => handlePlayerAction('all-in')}
              disabled={isProcessing}
              data-action="all-in"
              style={isAoFMode ? { 
                background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                flex: 1,
                fontSize: '16px',
              } : undefined}
            >
              ALL-IN {currentPlayer.chips.toFixed(2)}
            </button>
          )}

          {/* Fold Button - folding when checking is FREE gets a warning
              (classic beginner leak: BB folding to no raise). */}
          {possibleActions.includes('fold') && (
            <button
              className="action-btn fold-btn"
              onClick={() => {
                if (callAmount === 0) {
                  setConfirmFreeFold(true);
                  return;
                }
                handlePlayerAction('fold');
              }}
              disabled={isProcessing}
              data-action="fold"
              style={isAoFMode ? {
                flex: 1,
                fontSize: '16px',
              } : undefined}
            >
              FOLD
            </button>
          )}

          {/* Free-fold confirmation overlay */}
          {confirmFreeFold && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9300,
              background: 'rgba(0,0,0,0.6)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                background: 'rgba(13, 31, 46, 0.98)', borderRadius: 14,
                border: '1px solid rgba(245, 158, 11, 0.55)',
                padding: '20px 24px', maxWidth: 360, width: '90%',
                boxShadow: '0 12px 48px rgba(0,0,0,0.6)', color: '#e2e8f0',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#fbbf24', marginBottom: 8 }}>
                  Checking is free here
                </div>
                <div style={{ fontSize: 13.5, color: '#cbd5e1', marginBottom: 16, lineHeight: 1.6 }}>
                  There is no bet to call - you can see the next card without
                  paying anything. Folding gives up the pot for free.
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button
                    onClick={() => { setConfirmFreeFold(false); handlePlayerAction('check'); }}
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff', border: 'none', borderRadius: 8,
                      padding: '10px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                    }}
                  >
                    Check Instead
                  </button>
                  <button
                    onClick={() => { setConfirmFreeFold(false); handlePlayerAction('fold'); }}
                    style={{
                      background: 'transparent', color: '#f87171',
                      border: '1px solid rgba(248,113,113,0.5)', borderRadius: 8,
                      padding: '10px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                    }}
                  >
                    Fold Anyway
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* AoF Mode Indicator */}
          {isAoFMode && (
            <div style={{
              position: 'absolute',
              top: '-30px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}>
               ALL-IN OR FOLD MODE

            </div>
          )}
        </div>
      )}

      {/* Raise Slider (when shown) */}
      {showRaiseSlider && currentPlayer && (
        <div className="raise-slider-container">
          <div className="raise-slider-content">
            <input
              type="range"
              min={minRaise}
              max={currentPlayer.chips}
              value={raiseAmount}
              onChange={(e) => setRaiseAmount(Number(e.target.value))}
              className="raise-slider"
            />
            <div className="raise-amount-input-row">
              <span className="raise-dollar-sign">$</span>
              <input
                type="number"
                min={0}
                max={currentPlayer.chips}
                value={raiseAmount || ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? 0 : Number(e.target.value);
                  setRaiseAmount(Math.max(0, Math.min(val, currentPlayer.chips)));
                }}
                onBlur={(e) => {
                  // On blur, enforce minimum raise
                  if (raiseAmount < minRaise && raiseAmount !== 0) {
                    setRaiseAmount(minRaise);
                  }
                }}
                placeholder={`Min: $${minRaise}`}
                className="raise-amount-input"
              />
            </div>
            <div className="raise-preset-buttons">
              <button 
                className="preset-btn"
                onClick={() => setRaiseAmount(Math.min(minRaise, currentPlayer.chips))}
              >
                Min
              </button>
              <button
                className="preset-btn"
                onClick={() => setRaiseAmount(Math.min(Math.max(Math.round((gameState.pot + callAmount) / 2), minRaise), currentPlayer.chips))}
              >
                ½ Pot
              </button>
              <button
                className="preset-btn"
                onClick={() => setRaiseAmount(Math.min(Math.max(Math.round(gameState.pot + callAmount), minRaise), currentPlayer.chips))}
              >
                Pot
              </button>
              <button 
                className="preset-btn allin-preset"
                onClick={() => setRaiseAmount(currentPlayer.chips)}
              >
                ALL-IN
              </button>
            </div>
            <div className="raise-actions">
              <button 
                className="raise-confirm-btn"
                onClick={() => {
                  // A full-stack raise IS an all-in - send the dedicated action
                  // so the server shoves the exact stack (raise carries an
                  // increment-above-maxBet and would overshoot).
                  if (raiseAmount >= currentPlayer.chips) {
                    handlePlayerAction('all-in');
                    return;
                  }
                  if (raiseAmount < minRaise) {
                    setActionError(`Minimum raise is $${minRaise.toFixed(2)}`);
                    return;
                  }
                  handlePlayerAction('raise');
                }}
                disabled={isProcessing || raiseAmount <= 0}
              >
                {raiseAmount >= currentPlayer.chips ? `ALL-IN $${currentPlayer.chips.toFixed(2)}` : `RAISE TO $${(maxBet + raiseAmount).toFixed(2)}`}
              </button>
              <button 
                className="raise-cancel-btn"
                onClick={() => setShowRaiseSlider(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waiting for turn message */}
      {!isCurrentPlayerTurn && currentPlayer && !currentPlayer.eliminated && !currentPlayer.folded
        && gameState.round !== 'ended' && gameState.round !== 'waiting' && currentTurnPlayer
        && !(gameState as any).actionsLocked && !(gameState as any).runItTwiceOffer && !(gameState as any).allInShowdown && (
        <div className="waiting-indicator">
          Waiting for {getPlayerDisplayName(currentTurnPlayer.id)}...
        </div>
      )}

      {/*  Winner announcement bar (shows after runout animation completes) */}

      {gameState.round === 'ended' && gameState.winners && gameState.winners.length > 0 &&
       !isRunoutInProgress &&
       // All-in hands: the bar must wait for the runout to have PLAYED.
       // phase 'none' passed the old !inProgress gate, so a server-instant
       // resolve flashed the winner before a single card was revealed.
       (!(gameState.allInOccurred || gameState.allInShowdown) || runoutPhase === 'complete') && (
        <div className="winner-announcement-bar">
          <span className="winner-trophy"></span>

          <span className="winner-text">
            {(() => {
              // Uncalled-bet returns ride in winners[] flagged `uncalled` - they are
    // a player's own chips coming back, never a win. Run-it-twice rows come
    // one per run per winner; merge them per player so the notice judges
    // the whole hand (a chopped run and a lost run can still be a loss).
    const winners = aggregateWinnerRows((gameState.winners || []).filter((w: any) => !w.uncalled));
              const hasMultipleWinners = winners.length > 1;
              const isRunItTwice = winners.some((w: any) => w.run !== undefined);
              const isTrueSplitPot = hasMultipleWinners && 
                !isRunItTwice && 
                winners.every((w: any) => w.hand === winners[0].hand);
              const totalPot = winners.reduce((sum: number, w: any) => sum + (w.amount || 0), 0);
              const primaryWinner = [...winners].sort((a: any, b: any) => (b.amount || 0) - (a.amount || 0))[0];
              
              if (isTrueSplitPot) {
                // TRUE SPLIT POT: Show all winners
                const winnerNames = winners.map((w: any) => getPlayerDisplayName(w.playerId)).join(' & ');
                const handDesc = winners[0].handDescription || (winners[0].hand && winners[0].hand !== 'Fold Win' ? winners[0].hand : null);
                return (
                  <>
                    {winnerNames} split ${totalPot.toFixed(2)}
                    {handDesc && ` with ${handDesc}`}
                  </>
                );
              } else if (isFoldWin) {
                // FOLD WIN
                return (
                  <>
                    {getPlayerDisplayName(primaryWinner.playerId)} wins ${primaryWinner.amount.toFixed(2)} (all others folded)
                  </>
                );
              } else {
                // SINGLE WINNER or SIDE POTS - show primary winner with full description
                const handDesc = primaryWinner.handDescription || (primaryWinner.hand && primaryWinner.hand !== 'Fold Win' ? primaryWinner.hand : null);
                const loserDesc = primaryWinner.loserHandDescription;
                return (
                  <>
                    {getPlayerDisplayName(primaryWinner.playerId)} wins ${primaryWinner.amount.toFixed(2)}
                    {handDesc && ` with ${handDesc}`}
                    {loserDesc && <span style={{ opacity: 0.6, fontSize: '0.85em' }}> vs {loserDesc}</span>}
                  </>
                );
              }
            })()}
          </span>
          <span className="next-hand-timer">
            {nextHandCountdown > 0 ? `Next hand in ${nextHandCountdown}s...` : 'Dealing...'}
          </span>
        </div>
      )}

      {/* 🐰 Rabbit Hunt - only for players who were dealt in, when the hand
          ended before the full board and isn't run-it-twice */}
      {gameState.round === 'ended'
        && (gameState.communityCards || []).length < 5
        && !gameState.runItTwiceActive
        && !!safePlayers.find(p => p?.id === currentUserId) && (
        <div style={{
          position: 'fixed', bottom: '18px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 1200,
        }}>
          <button
            onClick={() => setRabbitHandNumber(gameState.handNumber || 0)}
            style={{
              background: 'linear-gradient(135deg, #d4af37, #b8941f)',
              color: '#1a1a1a',
              border: 'none',
              borderRadius: 20,
              padding: '8px 18px',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(212, 175, 55, 0.35)',
            }}
          >
            🐰 Rabbit Hunt
          </button>
        </div>
      )}

      {/* Error display */}
      {actionError && (
        <div className="action-error-toast">{actionError}</div>
      )}

      {/* Processing indicator */}
      {isProcessing && (
        <div className="processing-indicator">Processing...</div>
      )}

      {/*  GAME ITEMS BUTTON */}

      <button
        className="items-toggle-btn"
        onClick={() => setShowItemsMenu(!showItemsMenu)}
        title="Game Items"
      >
        <Gift size={20} />
        {pkCashBalance > 0 && (
          <span className="balance-badge">${pkCashBalance.toFixed(2)}</span>
        )}
      </button>

      {/*  INVITE FRIENDS BUTTON */}

      <button
        className="invite-btn"
        onClick={handleInvite}
        title="Invite Friends"
      >
        <Users size={14} style={{ marginRight: 4 }} /> Invite
      </button>

      {/*  RULES INFO BUTTON */}

      <button
        className="rules-info-btn"
        onClick={() => setShowRulesModal(true)}
        title="Table Rules & Hand Rankings"
        style={{
          position: 'fixed',
          bottom: '200px',
          right: '16px',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0D3847 0%, #1A2E3B 100%)',
          border: '2px solid #00D9D9',
          color: '#00D9D9',
          fontSize: '20px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          boxShadow: '0 4px 15px rgba(0, 217, 217, 0.3)',
          transition: 'all 0.2s',
        }}
      >
        ℹ

      </button>

      {/*  INVITE PLAYERS MODAL */}

      <UserSearch
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        currentUid={currentUserId}
        currentUserName={getPlayerDisplayName(currentUserId)}
        tableId={tableId}
        tableName="Poker Table"
      />

      {/*  GAME ITEMS MENU */}

      {showItemsMenu && (
        <GameItemsMenu
          tableId={tableId}
          currentUserId={currentUserId}
          currentUserName={getPlayerDisplayName(currentUserId)}
          players={safePlayers.map(p => ({
            id: p.id,
            name: getPlayerDisplayName(p.id),
          })).filter(p => p.id !== currentUserId)}
          pkCashBalance={pkCashBalance}
          onClose={() => setShowItemsMenu(false)}
          onBalanceChange={handleBalanceChange}
        />
      )}

      {/*  RULES MODAL */}

      <TableRulesModal
        isOpen={showRulesModal}
        onClose={() => setShowRulesModal(false)}
        specialRules={specialRules}
        tableName={tableName}
        smallBlind={gameState.smallBlind}
        bigBlind={gameState.bigBlind}
      />

      {/*  RUN IT TWICE MODAL */}

      {showRITModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderRadius: '20px',
            padding: '30px 40px',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 30px rgba(16, 185, 129, 0.2)',
            border: '2px solid rgba(16, 185, 129, 0.4)',
            maxWidth: '400px',
          }}>
            <div style={{ fontSize: '50px', marginBottom: '15px' }}></div>

            <h2 style={{ color: '#10b981', margin: '0 0 15px 0', fontSize: '24px' }}>
              Run It Twice?
            </h2>
            <p style={{ color: '#ccc', marginBottom: '20px', fontSize: '14px', lineHeight: '1.5' }}>
              All players are all-in! Would you like to run the remaining cards twice and split the pot between both runs?
              {ritCountdown !== null && (
                <span style={{ display: 'block', marginTop: 8, color: ritCountdown <= 5 ? '#fca5a5' : '#fbbf24', fontWeight: 700 }}>
                  Runs once in {ritCountdown}s if you do not choose
                </span>
              )}
            </p>
            <p style={{ color: '#f59e0b', marginBottom: '20px', fontSize: '13px', fontWeight: 'bold' }}>
               ALL players must accept for Run It Twice

            </p>
            
            {/* Show who has responded */}
            {gameState.runItTwiceOffer?.responses && Object.keys(gameState.runItTwiceOffer.responses).length > 0 && (
              <div style={{ marginBottom: '20px', padding: '10px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                <p style={{ color: '#888', fontSize: '12px', margin: '0 0 8px 0' }}>Responses:</p>
                {gameState.runItTwiceOffer.players?.map((odid: string) => {
                  const response = gameState.runItTwiceOffer.responses[odid];
                  const playerName = usernames[odid] || odid.substring(0, 8);
                  return (
                    <div key={odid} style={{ 
                      color: response === true ? '#10b981' : response === false ? '#ef4444' : '#888',
                      fontSize: '12px',
                      marginBottom: '4px',
                    }}>
                      {playerName}: {response === true ? ' Accepted' : response === false ? ' Declined' : '⏳ Waiting...'}

                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Only show buttons if user hasn't responded */}
            {gameState.runItTwiceOffer?.responses?.[currentUserId] === undefined ? (
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                <button
                  onClick={() => handleRITResponse(false)}
                  disabled={ritResponding}
                  style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                    color: '#fff',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: ritResponding ? 'not-allowed' : 'pointer',
                    opacity: ritResponding ? 0.6 : 1,
                  }}
                >
                  Run Once
                </button>
                <button
                  onClick={() => handleRITResponse(true)}
                  disabled={ritResponding}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: ritResponding ? 'not-allowed' : 'pointer',
                    opacity: ritResponding ? 0.6 : 1,
                  }}
                >
                  {ritResponding ? '⏳...' : 'Run It Twice'}
                </button>
              </div>
            ) : (
              <div style={{ color: '#10b981', fontSize: '14px' }}>
                 Your response recorded. Waiting for other players...

              </div>
            )}
          </div>
        </div>
      )}

      {/*  STRADDLE BUTTON - Show when between hands and straddle is enabled */}

      {canStraddle && !straddlePosted && (
        <div style={{
          position: 'absolute',
          bottom: '180px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 50,
        }}>
          <button
            onClick={handlePostStraddle}
            disabled={postingStraddle}
            style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: '#fff',
              border: '2px solid #a78bfa',
              padding: '10px 25px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: postingStraddle ? 'not-allowed' : 'pointer',
              opacity: postingStraddle ? 0.6 : 1,
              boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {postingStraddle ? '⏳...' : ` Post Straddle ($${(gameState.bigBlind || 2) * 2})`}

          </button>
        </div>
      )}

      {/*  STRADDLE POSTED INDICATOR */}

      {straddlePosted && (
        <div style={{
          position: 'absolute',
          bottom: '180px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 50,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: '#fff',
          padding: '8px 20px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold',
        }}>
           Straddle Posted!

        </div>
      )}

      {/*  REBUY BUTTON - Shows when current player needs to rebuy */}

      {currentPlayer?.needsRebuy && !sessionMode && (
        <div style={{
          position: 'absolute',
          bottom: '180px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
        }}>
          <button
            onClick={() => setShowRebuyModal(true)}
            disabled={isRebuyProcessing}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              border: '2px solid #34d399',
              padding: '14px 32px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isRebuyProcessing ? 'not-allowed' : 'pointer',
              opacity: isRebuyProcessing ? 0.6 : 1,
              boxShadow: '0 4px 20px rgba(16, 185, 129, 0.5)',
              animation: 'pulse 2s infinite',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {isRebuyProcessing ? '⏳ Processing...' : ' REBUY NOW'}

          </button>
        </div>
      )}

      {/*  REBUY MODAL */}

      <BuyInModal
        sessionMode={sessionMode}
        isOpen={showRebuyModal}
        onClose={() => setShowRebuyModal(false)}
        onConfirm={handleRebuy}
        minBuyIn={gameState.bigBlind ? gameState.bigBlind * 20 : 20}
        maxBuyIn={gameState.bigBlind ? gameState.bigBlind * 100 : 100}
        currency={gameState.currency || 'playcoins'}
        userId={currentUserId}
      />

      {/* 🐰 Rabbit Hunt Modal */}
      <RabbitHuntModal
        open={rabbitHandNumber !== null}
        onClose={() => setRabbitHandNumber(null)}
        tableId={tableId}
        handNumber={rabbitHandNumber || 0}
      />

      {/* Chat Toggle Button */}
      <button 
        className={`chat-toggle-btn ${unreadMessages > 0 ? 'has-unread' : ''}`}
        onClick={() => {
          setIsChatOpen(!isChatOpen);
          if (!isChatOpen) setUnreadMessages(0);
        }}
      >
        <MessageCircle size={20} />
        {unreadMessages > 0 && (
          <span className="unread-badge">{unreadMessages > 9 ? '9+' : unreadMessages}</span>
        )}
      </button>

      {/* DEAL ANIMATION - new-hand card burst + DEALING HAND #N label,
          same component the premium table mounts. */}
      <DealAnimation handNumber={gameState.handNumber} suppressUntil={(gameState as any).handStartsAt} />

      {/* BOT CHAT TOAST - top-left corner, ported from premium table */}
      {botChatBubble && (
        <div
          style={{
            position: 'absolute',
            top: '64px',
            left: '16px',
            maxWidth: '300px',
            background: 'rgba(15, 23, 42, 0.96)',
            border: '1px solid rgba(245, 158, 11, 0.6)',
            borderLeft: '4px solid #f59e0b',
            borderRadius: '10px',
            padding: '10px 14px',
            zIndex: 300,
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.45)',
            cursor: 'pointer',
            animation: 'pssBubbleLife 4.5s ease forwards',
          }}
          onClick={() => setBotChatBubble(null)}
        >
          <div style={{
            fontSize: '11px', fontWeight: 700, color: '#f59e0b',
            marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>{botChatBubbleName} says</div>
          <div style={{
            fontSize: '13px', color: '#e2e8f0', lineHeight: '1.4', fontWeight: 500,
          }}>{botChatBubble}</div>
        </div>
      )}

      {/* Table Chat */}
      <TableChat
        tableId={tableId}
        currentUserId={currentUserId}
        currentUserName={getPlayerDisplayName(currentUserId)}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        onNewMessage={() => {
          if (!isChatOpen) {
            setUnreadMessages(prev => prev + 1);
          }
        }}
      />

      {/* CSS Animations for Special Rules */}
      <style>{`
        @keyframes bountyPop {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
          50% { transform: translate(-50%, -50%) scale(1.1); }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.02); }
        }
      `}</style>
    </div>
  );
};

export default PokerTable;